import { config, userManager } from './auth';

/** Formy prawne (E18-S1) — słownik reguł żyje w API (`GET /api/legal-forms`); tu tylko kody. */
export type LegalForm =
  | 'JDG'
  | 'SP_CYWILNA'
  | 'SP_JAWNA'
  | 'SP_PARTNERSKA'
  | 'SP_KOMANDYTOWA'
  | 'SP_KOMANDYTOWO_AKCYJNA'
  | 'SP_ZOO'
  | 'PROSTA_SA'
  | 'SA'
  | 'SPOLDZIELNIA'
  | 'STOWARZYSZENIE'
  | 'FUNDACJA';

export const LEGAL_FORMS: LegalForm[] = [
  'JDG',
  'SP_CYWILNA',
  'SP_JAWNA',
  'SP_PARTNERSKA',
  'SP_KOMANDYTOWA',
  'SP_KOMANDYTOWO_AKCYJNA',
  'SP_ZOO',
  'PROSTA_SA',
  'SA',
  'SPOLDZIELNIA',
  'STOWARZYSZENIE',
  'FUNDACJA',
];

/** Podatek płacą osoby fizyczne (PIT) — właściciel albo wspólnicy; inaczej jednostka w CIT. */
export function personTaxed(form: LegalForm): boolean {
  return form === 'JDG' || form === 'SP_CYWILNA' || form === 'SP_JAWNA' || form === 'SP_PARTNERSKA';
}

/** Właściciele podlegają ZUS jak przedsiębiorcy — JDG i wspólnicy spółek osobowych. */
export function ownersInsured(form: LegalForm): boolean {
  return personTaxed(form) || form === 'SP_KOMANDYTOWA';
}

export type Company = {
  id: string;
  nip: string;
  legalForm: LegalForm;
  name: string;
  city: string | null;
  businessStartedOn: string | null;
  roles: string[];
  /**
   * Sposób rozliczenia obowiązujący dziś. Przychodzi razem z listą firm, żeby karta firmy
   * odróżniała je od siebie czymś więcej niż nazwą — bez tego biuro rachunkowe z kilkudziesięcioma
   * klientami musiałoby dopytywać o ustawienia osobno dla każdej pozycji.
   *
   * Puste, gdy firma nie ma jeszcze żadnych ustawień.
   */
  taxationForm?: TaxationForm | null;
  booksType?: BooksType | null;
  vatStatus?: VatStatus | null;
};

export type Account = {
  id: string;
  email: string;
  fullName: string;
  uiLocale: string | null;
  emailDeliverable: boolean;
  /** Fałsz, gdy adres czeka na potwierdzenie kodem — wtedy nie da się założyć firmy */
  emailVerified: boolean;
};

export type TaxationForm = 'RYCZALT' | 'SKALA' | 'LINIOWY' | 'CIT';
export type BooksType = 'RYCZALT' | 'KPIR' | 'FULL';
export type VatStatus = 'ACTIVE_MONTHLY' | 'ACTIVE_QUARTERLY' | 'EXEMPT';
export type ZusScheme = 'ULGA_NA_START' | 'PREFERENCYJNY' | 'MALY_ZUS_PLUS' | 'PELNY' | 'BRAK';

/** Okres zaliczek na podatek dochodowy; kwartalne przysługują małemu podatnikowi. */
export type AdvancePeriod = 'MONTHLY' | 'QUARTERLY';

export type CompanySettings = {
  id: string;
  validFrom: string;
  taxationForm: TaxationForm;
  booksType: BooksType;
  vatStatus: VatStatus;
  vatExemptionBasis: string | null;
  zusScheme: ZusScheme | null;
  cashMethod: boolean;
  smallTaxpayer: boolean;
  /** Chorobowe przedsiębiorcy jest dobrowolne i zmienia kwotę składek */
  sicknessInsurance: boolean;
  /** Miesięczne albo kwartalne zaliczki na podatek dochodowy — niezależnie od okresu VAT */
  incomeTaxPeriod: AdvancePeriod;
};

export type NewSettings = Omit<CompanySettings, 'id'>;

export type CompanyRole = 'OWNER' | 'ADMIN' | 'ACCOUNTANT' | 'EMPLOYEE' | 'BOARD_MEMBER' | 'VIEWER';

export type Member = {
  membershipId: string;
  userId: string;
  email: string;
  fullName: string;
  role: CompanyRole;
  validFrom: string;
  validTo?: string | null;   // pole znika z odpowiedzi, gdy dostęp nadal trwa
  /** Pracownik widzi wyłącznie dokumenty, które sam wprowadził. */
  ownDocumentsOnly: boolean;
};

export type Invitation = {
  id: string;
  email: string;
  role: CompanyRole;
  invitedByName: string;
  expiresAt: string;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
};

export type InvitationPreview = {
  companyName: string;
  role: CompanyRole;
  invitedByName: string;
  email: string;
  expiresAt: string;
};

export type CounterpartyType = 'DOMESTIC' | 'EU' | 'NON_EU' | 'PERSON';

export type Counterparty = {
  id: string;
  type: CounterpartyType;
  name: string;
  nip: string | null;
  vatEuId: string | null;
  country: string;
  addressLine: string | null;
  postalCode: string | null;
  city: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  active: boolean;
};

export type NewCounterparty = Omit<Counterparty, 'id'>;

export type VatRateCode = '23' | '8' | '5' | '0' | 'zw' | 'np' | 'oo';

export type Product = {
  id: string;
  code: string | null;
  names: Record<string, string>;
  unit: string;
  vatRate: VatRateCode;
  gtuCode: string | null;
  pkwiu: string | null;
  cn: string | null;
  defaultPrice: string | null;
  currency: string;
  active: boolean;
};

export type NewProduct = Omit<Product, 'id'>;

export type DocumentType = 'INVOICE' | 'CORRECTION' | 'PROFORMA' | 'RECEIPT';

/** Kiedy licznik wraca do jedynki: raz w roku albo pierwszego dnia każdego miesiąca. */
export type NumberingReset = 'YEAR' | 'MONTH';

export type NumberingSeries = {
  documentType: DocumentType;
  year: number;
  pattern: string;
  reset: NumberingReset;
  /** Ile numerów zużyto w całym roku — przy numeracji miesięcznej liczników jest dwanaście. */
  issued: number;
  nextNumber: number;
  preview: string;
};

export type AccountPurpose = 'MAIN' | 'VAT' | 'TAX_REFUND';

export type BankAccount = {
  id: string;
  iban: string;
  ibanFormatted: string;
  currency: string;
  purpose: AccountPurpose;
  bankName: string | null;
  description: string | null;
  active: boolean;
};

export type InvoiceStatus = 'DRAFT' | 'ISSUED' | 'CANCELLED';
export type DocumentKind = 'INVOICE' | 'CORRECTION';
/** Pomyłka wraca do okresu faktury pierwotnej, zdarzenie późniejsze rozlicza się na bieżąco */
export type CorrectionCause = 'MISTAKE' | 'EVENT';
export type PriceMode = 'NET' | 'GROSS';

export type InvoiceLine = {
  id?: string;
  position?: number;
  productId?: string | null;
  name: string;
  unit: string;
  /** Kwoty i ilości jako tekst — dokładnie tak, jak policzył je serwer. */
  quantity: string;
  unitPrice: string;
  vatRate: VatRateCode;
  gtuCode?: string | null;
  pkwiu?: string | null;
  lineAmount?: string;
  /** Wymagana u firm na ryczałcie: decyduje o stawce podatku od tego przychodu */
  ryczaltRate?: RyczaltRateCode | null;
};

/** Stawki ryczałtu od przychodów ewidencjonowanych */
export const RYCZALT_RATES = ['17', '15', '14', '12.5', '12', '10', '8.5', '5.5', '3', '2'] as const;
export type RyczaltRateCode = (typeof RYCZALT_RATES)[number];

export type EvidenceRow = {
  entryDate: string;
  documentNumber: string | null;
  counterpartyName: string | null;
  ryczaltRate: RyczaltRateCode;
  amount: string;
};

export type RyczaltEvidence = {
  from: string;
  to: string;
  rows: EvidenceRow[];
  byRate: Record<string, string>;
  total: string;
};

export type VatSummaryRow = { vatRate: VatRateCode; net: string; vat: string; gross: string };

export type Invoice = {
  id: string;
  number: string | null;
  status: InvoiceStatus;
  issueDate: string;
  saleDate: string;
  dueDate: string | null;
  counterpartyId: string | null;
  sellerSnapshot: Record<string, unknown> | null;
  buyerSnapshot: Record<string, unknown> | null;
  priceMode: PriceMode;
  currency: string;
  exchangeRate: string;
  paymentMethod: string | null;
  bankAccountId: string | null;
  totalNet: string;
  totalVat: string;
  totalGross: string;
  notes: string | null;
  /** Notatka wewnętrzna firmy — nie ma jej na wydruku ani w pliku KSeF (V063). */
  internalNote: string | null;
  issuedAt: string | null;
  documentKind: DocumentKind;
  correctedInvoiceId: string | null;
  correctionCause: CorrectionCause | null;
  correctionReason: string | null;
  lines: InvoiceLine[];
  vatSummary: VatSummaryRow[];
};

export type CorrectionRequest = {
  issueDate: string;
  cause: CorrectionCause;
  reason: string;
  /** Pozycje opisują stan po korekcie; różnicę liczy serwer */
  lines: InvoiceLine[];
};

export type InvoiceDraft = {
  issueDate: string;
  saleDate: string;
  dueDate?: string | null;
  counterpartyId?: string | null;
  priceMode: PriceMode;
  currency: string;
  exchangeRate?: string;
  bankAccountId?: string | null;
  paymentMethod?: string | null;
  notes?: string | null;
  lines: InvoiceLine[];
};

export type OfficeRole = 'OFFICE_OWNER' | 'OFFICE_ADMIN' | 'ACCOUNTANT' | 'ASSISTANT';
export type AssignmentRole = 'LEAD' | 'BACKUP' | 'VIEWER';

export type Office = { id: string; nip: string; name: string; myRole: OfficeRole | null };

export type OfficeMember = {
  membershipId: string;
  userId: string;
  email: string;
  fullName: string;
  role: OfficeRole;
  validFrom: string;
  validTo?: string | null;
};

export type OfficeClient = {
  id: string;
  companyId: string;
  companyName: string;
  companyNip: string;
  officeName: string;
  /** Kto z biura ma dziś dostęp do ksiąg — puste, dopóki biuro nikogo nie przydzieliło. */
  accountants: string[];
  payer: string;
  validFrom: string;
  validTo?: string | null;
  /** Umowa działa dopiero po zgodzie firmy; „pending" znaczy, że biuro jeszcze nic nie może. */
  status: 'pending' | 'accepted' | 'rejected';
};

/** Przydział osoby z biura do klienta — dopiero on otwiera dostęp do ksiąg tej firmy. */
export type ClientAssignment = {
  id: string;
  agreementId: string;
  companyId: string;
  companyName: string;
  userId: string;
  userName: string;
  role: AssignmentRole;
  validFrom: string;
  validTo?: string | null;
};

export type ClientMonthStatus = {
  companyId: string;
  companyName: string;
  companyNip: string;
  assignedTo: string[];
  settingsMissing: boolean;
  invoicesIssued: number;
  invoiceDrafts: number;
  costsBooked: number;
  costDrafts: number;
  revenue: string;
  costs: string;
  readyToSettle: boolean;
  warnings: string[];
};

export type OfficeDashboard = { month: string; clients: ClientMonthStatus[] };

export type CostCategory = 'GOODS' | 'PURCHASE_COSTS' | 'SALARIES' | 'OTHER';
export type VatDeduction = 'FULL' | 'HALF' | 'NONE';
export type CostStatus = 'DRAFT' | 'BOOKED';
export type CostDocumentKind = 'COST' | 'CORRECTION';

export type CostVatRow = { vatRate: VatRateCode; net: string; vat: string };

export type CostDocument = {
  id: string;
  documentNumber: string;
  counterpartyId: string | null;
  issueDate: string;
  costDate: string;
  category: CostCategory;
  kpirColumn: number;
  vatDeduction: VatDeduction;
  fixedAsset: boolean;
  /** Wewnątrzwspólnotowe nabycie towarów — wchodzi do informacji VAT-UE. */
  intraCommunity: boolean;
  currency: string;
  totalNet: string;
  totalVat: string;
  totalGross: string;
  deductibleVat: string;
  costAmount: string;
  status: CostStatus;
  description: string | null;
  vatRows: CostVatRow[];
  documentKind: CostDocumentKind;
  correctedDocumentId: string | null;
  correctionCause: CorrectionCause | null;
  correctionReason: string | null;
};

export type NewCostCorrection = {
  documentNumber: string;
  issueDate: string;
  costDate: string;
  cause: CorrectionCause;
  reason: string;
  /** Kwoty różnicy przepisane z dokumentu dostawcy — zwykle ujemne */
  vatRows: CostVatRow[];
  description?: string | null;
};

export type NewCost = {
  documentNumber: string;
  counterpartyId?: string | null;
  issueDate: string;
  costDate: string;
  /** Termin płatności — po nim idzie wiekowanie rozrachunku przy pełnych księgach. */
  dueDate?: string | null;
  category: CostCategory;
  vatDeduction: VatDeduction;
  /** Nabycie zaliczane do środków trwałych — deklaracja VAT wykazuje je osobno. */
  fixedAsset: boolean;
  /** Wewnątrzwspólnotowe nabycie towarów — wchodzi do informacji VAT-UE. */
  intraCommunity: boolean;
  description?: string | null;
  vatRows: CostVatRow[];
};

export type LedgerEntry = {
  id: string;
  entryDate: string;
  period: string;
  sourceType: 'INVOICE' | 'COST' | 'CORRECTION' | 'MANUAL';
  documentNumber: string | null;
  description: string;
  counterpartyName: string | null;
  kpirColumn: number | null;
  amount: string;
  currency: string;
};

export type LedgerReport = {
  from: string;
  to: string;
  entries: LedgerEntry[];
  totalRevenue: string;
  byKpirColumn: Record<string, string>;
};

export type VatRegisters = {
  from: string;
  to: string;
  quarterly: boolean;
  vatDue: string;
  vatDeductible: string;
  vatResult: string;
};

export type InventoryKind = 'OPENING' | 'CLOSING' | 'INTERIM';
export type InventoryStatus = 'DRAFT' | 'APPROVED';
export type ItemCategory = 'GOODS' | 'MATERIALS' | 'SEMI_FINISHED' | 'FINISHED' | 'WASTE';

export type InventoryItem = {
  id: string;
  position: number;
  name: string;
  category: ItemCategory;
  quantity: string;
  unit: string;
  unitPrice: string;
  value: string;
};

export type Inventory = {
  id: string;
  kind: InventoryKind;
  takenOn: string;
  status: InventoryStatus;
  totalValue: string;
  notes: string | null;
  items: InventoryItem[];
};

export type NewInventoryItem = {
  name: string;
  category: ItemCategory;
  quantity: string;
  unit: string;
  unitPrice: string;
};

export type AnnualIncomeWarning =
  | 'NO_CLOSING_INVENTORY'
  | 'NO_OPENING_INVENTORY'
  | 'DRAFT_INVENTORY'
  | 'NOT_KPIR';

export type AnnualIncome = {
  year: number;
  revenue: string;
  purchases: string;
  otherCosts: string;
  openingInventory: string;
  closingInventory: string;
  /** Remanent początkowy + zakupy − remanent końcowy */
  costOfGoodsSold: string;
  costsTotal: string;
  income: string;
  warnings: AnnualIncomeWarning[];
};

export type AssetKind = 'TANGIBLE' | 'INTANGIBLE';
export type DepreciationMethod = 'LINIOWA' | 'JEDNORAZOWA' | 'DEGRESYWNA';

export type FixedAsset = {
  id: string;
  name: string;
  inventoryNumber: string | null;
  kstCode: string | null;
  kind: AssetKind;
  acquiredOn: string;
  inServiceOn: string;
  initialValue: string;
  method: DepreciationMethod;
  annualRate: string;
  coefficient: string;
  disposedOn: string | null;
  disposalReason: string | null;
};

export type DepreciationEntry = { period: string; amount: string; remaining: string };

export type AssetWithPlan = {
  asset: FixedAsset;
  plan: DepreciationEntry[];
  bookedThrough: string | null;
  bookedTotal: string;
  /**
   * To samo w **dzienniku** (pełne księgi) — osobno od księgi, bo te dwie liczby mogą się różnić:
   * odpisy zaksięgowane, zanim amortyzacja trafiała do dziennika, stoją tylko w księdze.
   */
  postedThrough: string | null;
  postedTotal: string;
};

/** Środek pominięty w przebiegu amortyzacji razem z powodem. */
export type SkippedAsset = { name: string; inventoryNumber: string | null; reason: string };

/** Wynik przebiegu: `booked` to odpisy w księdze, `posted` — zapisy w dzienniku. */
export type DepreciationRun = {
  period: string;
  booked: number;
  total: string;
  posted: number;
  skipped: SkippedAsset[];
};

/** Wyksięgowanie środka z ksiąg przy likwidacji albo sprzedaży. */
export type WriteOff = { entryNo: number; accumulated: string; netValue: string };

/** Wycofanie z ewidencji razem ze skutkiem w księgach; `note` mówi, czego nie zaksięgowano. */
export type AssetDisposal = { asset: FixedAsset; writeOff: WriteOff | null; note: string | null };

export type NewAsset = {
  name: string;
  inventoryNumber?: string | null;
  kstCode?: string | null;
  kind: AssetKind;
  acquiredOn: string;
  inServiceOn: string;
  initialValue: string;
  method: DepreciationMethod;
  /** Stawka roczna jako ułamek: 0.20 to 20% */
  annualRate: string;
};

/**
 * Rozliczenie międzyokresowe kosztów (E7-S5) — polisa albo abonament rozłożony na miesiące.
 *
 * Kwoty i konta przychodzą z serwera; harmonogram (`plan`) też się tam liczy, bo to ten sam
 * rachunek, który wchodzi do ksiąg — przeliczanie go w przeglądarce dałoby drugą prawdę.
 */
export type CostAccrual = {
  id: string;
  costDocumentId: string;
  documentNumber: string;
  description: string;
  totalAmount: string;
  costAccount: string;
  accrualAccount: string;
  fromPeriod: string;
  toPeriod: string;
};

/** Rata harmonogramu; `entryNo` to numer zapisu w dzienniku — puste znaczy „jeszcze nie w księgach". */
export type AccrualInstalment = { period: string; amount: string; remaining: string; entryNo: number | null };

export type AccrualView = {
  accrual: CostAccrual;
  plan: AccrualInstalment[];
  releasedTotal: string;
  releasedThrough: string | null;
};

/** Pominięty harmonogram razem z powodem — przebieg nie milczy o tym, czego nie zaksięgował. */
export type SkippedAccrual = { documentNumber: string; description: string; reason: string };

export type AccrualRun = { period: string; booked: number; total: string; skipped: SkippedAccrual[] };

/**
 * Rozliczenie międzyokresowe bierne (art. 39 ust. 2 uor) — koszt, który dotyczy tego okresu,
 * a faktury jeszcze nie ma.
 *
 * `basis` jest obowiązkowe, bo kwota jest **szacunkiem**, nie odczytem z dokumentu: bez zdania
 * o tym, z czego wyszła, nie ma czym jej obronić przy badaniu sprawozdania.
 */
export type CostProvision = {
  id: string;
  description: string;
  basis: string;
  totalAmount: string;
  costAccount: string;
  provisionAccount: string;
  fromPeriod: string;
  toPeriod: string;
};

/** Rozliczenie rezerwy: ile z niej zeszło, kiedy i na jakie konto. */
export type ProvisionSettlement = {
  entryNo: number;
  postingDate: string;
  amount: string;
  account: string;
  description: string | null;
};

export type ProvisionView = {
  provision: CostProvision;
  plan: AccrualInstalment[];
  accruedTotal: string;
  accruedThrough: string | null;
  settlements: ProvisionSettlement[];
  settledTotal: string;
  /** Saldo rezerwy: naliczone minus rozliczone. Zero po zamknięciu sprawy. */
  balance: string;
};

export type ProvisionRun = { period: string; booked: number; total: string; skipped: SkippedAccrual[] };

export type NewProvision = {
  description: string;
  basis: string;
  totalAmount: string;
  fromPeriod: string;
  toPeriod: string;
  costAccount: string;
  provisionAccount?: string | null;
};

/** Puste `amount` znaczy „całe saldo", puste `account` — konto kosztowe, które naliczenie obciążało. */
export type SettleProvision = {
  postingDate: string;
  amount?: string | null;
  account?: string | null;
  note?: string | null;
};

export type NewAccrual = {
  costDocumentId: string;
  description?: string | null;
  totalAmount?: string | null;
  fromPeriod: string;
  toPeriod: string;
  costAccount?: string | null;
  accrualAccount?: string | null;
};

export type SettlementStatus = 'UNPAID' | 'PARTIAL' | 'PAID' | 'OVERPAID';

export type Settlement = {
  documentId: string;
  documentNumber: string | null;
  counterpartyName: string | null;
  issueDate: string;
  dueDate: string | null;
  /** Kwota należna po uwzględnieniu korekt */
  amountDue: string;
  amountPaid: string;
  remaining: string;
  currency: string;
  status: SettlementStatus;
  daysOverdue: number;
};

export type MatchConfidence = 'EXACT' | 'PROBABLE' | 'POSSIBLE';
export type MatchReason = 'NUMBER_AND_AMOUNT' | 'NUMBER_ONLY' | 'AMOUNT_ONLY' | 'COUNTERPARTY_AND_AMOUNT';

export type BankTransaction = {
  id: string;
  valueDate: string;
  bookingDate: string | null;
  /** Dodatnia kwota to wpływ, ujemna wypływ */
  amount: string;
  currency: string;
  reference: string | null;
  details: string;
  paymentId: string | null;
};

export type MatchProposal = {
  settlement: Settlement;
  confidence: MatchConfidence;
  reason: MatchReason;
};

export type TransactionWithProposals = {
  transaction: BankTransaction;
  proposals: MatchProposal[];
};

export type ImportResult = {
  statementId: string;
  imported: number;
  skipped: number;
  transactions: TransactionWithProposals[];
};

export type NewPayment = {
  paidOn: string;
  /** Kwota **w walucie dokumentu**: rozrachunek rozlicza się w tej walucie, nie w złotych. */
  amount: string;
  /**
   * Kurs z dnia zapłaty; wymagany przy dokumencie w walucie obcej.
   *
   * Bez niego serwer odmawia zapisu: kwota w euro zaksięgowana jako złotówki zafałszowałaby bilans
   * i podstawę podatku, a różnica kursowa nie miałaby z czego powstać.
   */
  exchangeRate?: string | null;
  method?: string | null;
  note?: string | null;
};

/** Jedna zapłata zapisana przy fakturze albo przy dokumencie kosztowym (E10). */
export type Payment = {
  id: string;
  direction: 'INCOMING' | 'OUTGOING';
  invoiceId: string | null;
  costDocumentId: string | null;
  paidOn: string;
  amount: string;
  currency: string;
  /** Kurs z dnia zapłaty i kwota w złotych — to ona poszła do dziennika. */
  exchangeRate: string | null;
  amountPln: string | null;
  method: string | null;
  note: string | null;
};

export type ReceivablesSummary = {
  count: number;
  total: string;
  overdueCount: number;
  overdueTotal: string;
};

export type Plan = 'RYCZALT' | 'KPIR' | 'FULL';
export type BillingPayer = 'COMPANY' | 'OFFICE';
export type SalesChannel = 'WEB' | 'APP_STORE' | 'GOOGLE_PLAY';
export type ZeroReason =
  | 'TRIAL'
  | 'NO_REVENUE'
  | 'BELOW_MINIMUM'
  | 'FREE_AFTER_LEAVING_OFFICE'
  | 'STORE_SUBSCRIPTION';

export type Fee = {
  period: string;
  plan: Plan;
  payer: BillingPayer;
  channel: SalesChannel;
  revenue: string;
  planPriceGross: string;
  /** Sufit „nigdy więcej niż 20% przychodu" — pokazujemy dla porównania */
  revenueCapGross: string;
  feeGross: string;
  zeroReason: ZeroReason | null;
  /** Ile działalności tej samej osoby płaci w tym miesiącu. */
  payingCompanies: number;
  /** Zastosowana zniżka wielofirmowa jako ułamek, np. „0.15". */
  multiCompanyDiscount: string;
};

export type BillingRecord = Omit<Fee, 'revenueCapGross'> & { id: string; settledAt: string | null };

export type AdjustmentKind = 'SURCHARGE' | 'CREDIT';
export type AdjustmentStatus = 'PENDING_CHARGE' | 'CHARGED' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED';

export type BillingAdjustment = {
  id: string;
  period: string;
  recordedFor: string;
  previousRevenue: string;
  currentRevenue: string;
  previousFee: string;
  currentFee: string;
  amount: string;
  kind: AdjustmentKind;
  status: AdjustmentStatus;
  decisionNote: string | null;
};

export type BillingMonth = { record: BillingRecord; adjustments: BillingAdjustment[] };

/** Kto płaci za Firmeto: firma sama albo biuro rachunkowe — ten sam zestaw adresów pod innym przedrostkiem. */
export type PayerScope = { kind: 'companies' | 'offices'; id: string };

export type PaymentMethodKind = 'card' | 'blik';

/** Operator płatności do wyboru; `defaultFor` — formy płatności, dla których jest domyślny. */
export type PaymentProvider = { provider: string; methods: string[]; defaultFor: string[] };

export type PaymentMandate = {
  id: string;
  provider: string;
  method: string;
  label: string | null;
  status: 'pending' | 'active' | 'failed' | 'revoked' | 'expired';
  expiresOn: string | null;
  createdAt: string;
};

export type PaymentCharge = {
  id: string;
  provider: string;
  period: string | null;
  amountGross: string;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed' | 'refunded';
  failureMessage: string | null;
  createdAt: string;
};

/** Zaległa opłata: od `readOnlyFrom` konto jest tylko do odczytu, dopóki nie zapłaci. */
export type PaymentArrear = { period: string; amountGross: string; reason: string; attempts: number; readOnlyFrom: string };

export type MandateStart = { redirectUrl: string; providerToken: string | null; label: string | null };

/** Rozliczenie biura za miesiąc: firmy z pakietu do sufitu i opłata z umowy indywidualnej ponad sufit. */
export type OfficeBillingStatement = {
  period: string;
  officePackage: string;
  ceilingGross: string | null;
  agreementFeeGross: string;
  agreementReference: string | null;
  subtotalGross: string;
  totalGross: string;
  cappedBy: string;
  rows: { companyId: string; companyName: string; companyNip: string; plan: string; revenue: string; feeGross: string; zeroReason: string | null }[];
};

export type ObligationKind = 'VAT' | 'PIT_ADVANCE' | 'CIT_ADVANCE' | 'ZUS' | 'HEALTH_ANNUAL';

/**
 * Zaliczka na CIT (E9-S2).
 *
 * Liczy się narastająco od początku roku obrotowego: podatek od dochodu do tego miesiąca minus
 * podatek do miesiąca poprzedniego. Dlatego `amountDue` bywa zerem także przy zysku — znaczy to,
 * że zaliczki za wcześniejsze miesiące pokryły już cały podatek.
 */
export type CitAdvance = {
  period: string;
  /** Okres rozliczenia: `2027-08` albo `2027-Q3` przy zaliczkach kwartalnych. */
  periodLabel: string;
  quarterly: boolean;
  baseYtd: string;
  rate: string;
  taxYtd: string;
  taxEarlier: string;
  amountDue: string;
  dueOn: string;
  /** Czego rachunek nie policzył: strata z lat ubiegłych, różnice przejściowe, limit stawki 9%. */
  notes: string[];
};

export type CitAnnual = {
  fiscalYearId: string;
  base: string;
  rate: string;
  taxForYear: string;
  taxFromAdvances: string;
  /** Do dopłaty przy CIT-8; ujemna znaczy nadpłatę. */
  difference: string;
  notes: string[];
};

export type Obligation = {
  kind: ObligationKind;
  /** Miesiąc (2027-08) albo kwartał (2027-Q3) przy kwartalnym VAT */
  period: string;
  /** Termin z ustawy; gdy wypada w dzień wolny, dueDate jest późniejszy */
  statutoryDate: string;
  dueDate: string;
  movedForHoliday: boolean;
};

export type CompanyMonthWarning =
  | 'NO_SETTINGS'
  | 'NO_DOCUMENTS'
  | 'OVERDUE_RECEIVABLES'
  | 'OVERDUE_PAYABLES'
  | 'NO_BOOKKEEPER'
  | 'VAT_EXEMPTION_NEAR_LIMIT'
  | 'VAT_EXEMPTION_EXCEEDED'
  | 'SUSPENDED';

/** Wspólnik spółki osobowej: udział w zysku i własne rozliczenie (E18-S2). */
export type Partner = {
  id: string;
  firstName: string;
  lastName: string;
  /** Ułamek: 0.5 to połowa. */
  profitShare: string;
  taxationForm: TaxationForm;
  zusScheme: ZusScheme | null;
  sicknessInsurance: boolean;
  fromDate: string;
  toDate: string | null;
};

export type PartnerSettlement = {
  partnerId: string;
  name: string;
  profitShare: string;
  taxationForm: TaxationForm;
  zus: ZusContributions | null;
  pitAdvance: PitAdvance | null;
  /** Wspólnicy spółki osobowej — każdy ze swoją zaliczką; przy JDG pusta lista. */
  partners: PartnerSettlement[];
  notes: SettlementNote[];
};

export type SettlementNote =
  | 'ESTIMATE'
  | 'RYCZALT_NOT_SUPPORTED'
  | 'MALY_ZUS_PLUS_APPROXIMATED'
  | 'UNKNOWN_YEAR'
  | 'COMPANY_TAX'
  | 'PARTNERS_SPLIT'
  | 'PARTNERS_MISSING'
  | 'PARTNERS_SHARE_MISMATCH'
  | 'BAD_DEBT_IN_INCOME'
  | 'BAD_DEBT_NOT_IN_RYCZALT'
  | 'MALY_ZUS_PLUS_FROM_PREVIOUS_YEAR'
  | 'MALY_ZUS_PLUS_AT_FLOOR'
  | 'MALY_ZUS_PLUS_AT_CAP'
  | 'MALY_ZUS_PLUS_REVENUE_OVER_LIMIT'
  | 'MALY_ZUS_PLUS_TOO_FEW_DAYS'
  | 'MALY_ZUS_PLUS_DAYS_ASSUMED'
  | 'MALY_ZUS_PLUS_FROM_REVENUE'
  | 'MALY_ZUS_PLUS_LIMIT_NOT_CHECKED'
  | 'MALY_ZUS_PLUS_SUSPENSION_DAYS'
  | 'SUSPENDED_MONTH'
  | 'SUSPENDED_PART_OF_MONTH';

export type ZusContributions = {
  socialBasis: string;
  pension: string;
  disability: string;
  sickness: string | null;
  accident: string;
  laborFund: string | null;
  health: string;
  total: string;
};

export type PitAdvance = {
  taxBase: string;
  taxYtd: string;
  advancesPaid: string;
  amount: string;
};

/** Limit zwolnienia z VAT narastająco od początku roku; tylko dla firmy zwolnionej */
export type VatExemptionStatus = { limit: string; used: string; remaining: string };

export type CompanyMonth = {
  period: string;
  revenue: string;
  costs: string;
  income: string;
  vatDue: string | null;
  vatDeductible: string | null;
  vatResult: string | null;
  vatExemption: VatExemptionStatus | null;
  receivables: ReceivablesSummary;
  /** Wiekowanie rozrachunków z dziennika — tylko pełne księgi z planem kont; KPiR ma `receivables`. */
  aging: SettlementAging | null;
  zus: ZusContributions | null;
  /** Zaliczka na CIT — tylko spółki na pełnych księgach. */
  cit: CitAdvance | null;
  pitAdvance: PitAdvance | null;
  /** Wspólnicy spółki osobowej — każdy ze swoją zaliczką; przy JDG pusta lista. */
  partners: PartnerSettlement[];
  notes: SettlementNote[];
  obligations: Obligation[];
  warnings: CompanyMonthWarning[];
};

export type AgingSide = {
  count: number;
  total: string;
  overdueCount: number;
  overdueTotal: string;
  byBucket: Record<'NOT_DUE' | 'DAYS_1_30' | 'DAYS_31_60' | 'DAYS_61_90' | 'OVER_90', string>;
};

export type SettlementAging = { asOf: string; fiscalYearId: string; receivables: AgingSide; payables: AgingSide };

export type PlanPrice = {
  plan: 'RYCZALT' | 'KPIR' | 'FULL';
  net: string;
  gross: string;
};

export type Pricing = {
  plans: PlanPrice[];
  vatRate: string;
  minimumFeeGross: string;
  revenueShareCap: string;
  trialMonths: number;
};

export type Registration = {
  email: string;
  password: string;
  fullName: string;
  uiLocale: string;
  /** Zgoda na regulamin i politykę prywatności — bez niej serwer konta nie założy. */
  acceptedTerms: boolean;
  /** Wiadomości o zmianach w przepisach; dobrowolne, zapisywane razem ze zgodą na regulamin. */
  wantsLawUpdates: boolean;
};

/** Urząd skarbowy ze słownika MF; nazwa wielkimi literami, tak jak w schemie. */
export type TaxOffice = {
  code: string;
  name: string;
  /** Dla największych podatników — ekran ostrzega przed wyborem. */
  specialised: boolean;
};

export type FilingDetails = {
  taxOfficeCode: string | null;
  firstName: string | null;
  lastName: string | null;
  birthDate: string | null;
  email: string | null;
  phone: string | null;
  /**
   * Kod tytułu ubezpieczenia do deklaracji ZUS DRA — sześć cyfr: cztery kodu, prawo do renty,
   * stopień niepełnosprawności. Wpisuje go człowiek, bo słownik ZUS-u daje jednoznaczny kod tylko
   * przy pełnych (`0510`) i preferencyjnych (`0570`) składkach.
   */
  zusInsuranceTitleCode: string | null;
};

export type NewCompany = {
  nip: string;
  legalForm: Company['legalForm'];
  name: string;
  addressLine?: string;
  postalCode?: string;
  city?: string;
  businessStartedOn?: string;
};

/** Dane firmy z rejestru publicznego — podpowiedź do formularza, nie źródło prawdy (E1-S1). */
export type CompanyFromRegistry = {
  nip: string;
  name: string;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  regon: string | null;
  krs: string | null;
  vatStatus: string | null;
  source: string;
  /** Odczytana z nazwy albo wprost z KRS-u; `null` znaczy „nie rozpoznaliśmy, wybierz sam”. */
  legalForm: Company['legalForm'] | null;
  /** Z KRS-u; wykaz podatników VAT tej daty nie zna, więc dla JDG zostaje pusta. */
  businessStartedOn: string | null;
};

/** Zamknięty miesiąc: po zamknięciu do okresu nie trafi żaden nowy zapis (E7-S6). */
export type ClosedPeriod = {
  period: string;
  closedAt: string;
  closedBy: string | null;
  reason: string | null;
};

export type ClosingCheck = {
  period: string;
  draftInvoices: number;
  futurePeriod: boolean;
  clean: boolean;
};

export type FiscalYear = {
  id: string;
  startsOn: string;
  endsOn: string;
  status: 'OPEN' | 'CLOSED';
  closedAt: string | null;
};

/**
 * Lista kontrolna zamknięcia roku (docs/08, etap 1).
 *
 * `detail` przychodzi po polsku z serwera i służy podglądowi w panelu operatora; ekran klienta
 * pisze o punktach w języku użytkownika, po kodzie `check` — dlatego oba są w odpowiedzi.
 */
/**
 * Zawieszenie wykonywania działalności (art. 22–25 ustawy Prawo przedsiębiorców).
 *
 * Okres, nie znacznik: firma zawiesza i podejmuje działalność wiele razy. Pusta data zakończenia
 * znaczy „nadal zawieszona" — w CEIDG zawieszenie nie ma górnej granicy czasu.
 */
export type BusinessSuspension = {
  id: string;
  startsOn: string;
  endsOn: string | null;
  note: string | null;
};

export type SuspensionView = {
  suspension: BusinessSuspension;
  /** Ile dni objęło zawieszenie; przy otwartym — do dziś. */
  days: number;
  open: boolean;
  /** Krótsze niż 30 dni, więc prawdopodobnie pomyłka w dacie (art. 23 ust. 1). */
  tooShort: boolean;
};

export type NewSuspension = { startsOn: string; endsOn?: string | null; note?: string | null };

export type ReadinessCheck =
  | 'months_open'
  | 'unposted_invoices'
  | 'unposted_costs'
  | 'trial_balance'
  | 'accounts_without_position'
  | 'opening_balance'
  | 'draft_invoices'
  | 'inbox_documents'
  | 'settlements_without_counterparty'
  | 'depreciation'
  | 'closing_inventory'
  | 'open_provisions'
  | 'result_transfer';

export type ReadinessItem = {
  check: ReadinessCheck;
  severity: 'blocking' | 'warning';
  passed: boolean;
  count: number;
  detail: string | null;
};

export type YearReadiness = {
  fiscalYearId: string;
  status: FiscalYear['status'];
  ready: boolean;
  warnings: number;
  items: ReadinessItem[];
};

/** Co powstało przy zamknięciu roku: zapis przeksięgowania wyniku i sam wynik. */
export type YearClosing = {
  fiscalYearId: string;
  resultEntryNo: number | null;
  result: string | null;
  acceptedWarnings: ReadinessCheck[];
};

/** Zakładowy plan kont (E7-S1). Plan należy do roku obrotowego, nie do firmy. */
export type AccountKind = 'BALANCE' | 'RESULT' | 'OFFBALANCE';
export type AccountSide = 'DEBIT' | 'CREDIT' | 'BOTH' | 'NONE';
export type TaxTreatment = 'DEDUCTIBLE' | 'NON_DEDUCTIBLE' | 'TAXABLE' | 'NON_TAXABLE';
export type StatementTemplate = 'ZAL_1' | 'ZAL_4' | 'ZAL_5';
export type CostVariant = 'BY_KIND' | 'BY_FUNCTION' | 'BOTH';
export type TemplateScope = 'SYSTEM' | 'OFFICE' | 'COMPANY';
export type ChartSource = 'TEMPLATE' | 'PREVIOUS_YEAR' | 'EMPTY';

/** Rola konta w automatach księgujących; zbiór zamknięty, bo schematy księgowania po niej pytają. */
export type AccountRole =
  | 'RECEIVABLES' | 'PAYABLES'
  | 'VAT_DUE' | 'VAT_INPUT' | 'VAT_SETTLEMENT'
  | 'SALES_SERVICES' | 'SALES_GOODS' | 'COST_OF_GOODS'
  | 'GOODS'
  | 'COSTS_MATERIALS' | 'COSTS_SERVICES' | 'COSTS_TAXES' | 'COSTS_SALARIES' | 'COSTS_SOCIAL' | 'COSTS_OTHER'
  | 'FIXED_ASSETS' | 'INTANGIBLE_ASSETS'
  | 'ACCUMULATED_DEPRECIATION' | 'ACCUMULATED_AMORTISATION' | 'DEPRECIATION_COST'
  | 'CASH' | 'BANK'
  | 'INCOME_TAX' | 'SOCIAL_SECURITY' | 'PAYROLL'
  | 'RESULT' | 'PRIOR_YEARS_RESULT';

export type ChartAccount = {
  id: string;
  number: string;
  name: string;
  /** Zespół 0–9 — pierwsza cyfra numeru; po niej idzie układ planu na ekranie. */
  group: number;
  kind: AccountKind;
  side: AccountSide;
  settlement: boolean;
  currencyAccount: boolean;
  taxTreatment: TaxTreatment | null;
  systemRole: string | null;
  tags: string[];
  /** Konto z analityką jest sumą swoich dzieci i nie przyjmuje zapisów. */
  postable: boolean;
  hasChildren: boolean;
  active: boolean;
  previousAccountId: string | null;
  /** Pozycje sprawozdania dla salda Wn i Ma (kody ze słownika pozycji); null — nieprzypisane. */
  statementPositionDebit: string | null;
  statementPositionCredit: string | null;
};

/** Konto takie, jakie powstanie po wybraniu źródła — jeszcze bez identyfikatora. */
export type ChartPreviewAccount = {
  number: string;
  name: string;
  group: number;
  kind: AccountKind;
  side: AccountSide;
  settlement: boolean;
  currencyAccount: boolean;
  taxTreatment: TaxTreatment | null;
  systemRole: string | null;
  statementPositionDebit: string | null;
  statementPositionCredit: string | null;
};

export type Chart = {
  fiscalYearId: string;
  statementTemplate: StatementTemplate;
  costVariant: CostVariant;
  sourceTemplateId: string | null;
  accounts: ChartAccount[];
};

export type ChartTemplateInfo = {
  id: string;
  scope: TemplateScope;
  name: string;
  description: string | null;
  statementTemplate: StatementTemplate;
  costVariant: CostVariant;
};

export type AccountTagInfo = {
  code: string;
  dimension: 'BALANCE_SHEET' | 'INCOME_STATEMENT' | 'TAX' | 'GROUP';
  name: string;
  statementTemplate: StatementTemplate | null;
};

export type NewAccount = {
  number: string;
  name: string;
  kind: AccountKind;
  side: AccountSide;
  settlement: boolean;
  currencyAccount: boolean;
  taxTreatment: TaxTreatment | null;
  systemRole: AccountRole | null;
  tags: string[];
  active: boolean;
  statementPositionDebit: string | null;
  statementPositionCredit: string | null;
};

/**
 * Pozycja bilansu albo RZiS z załącznika do ustawy o rachunkowości (słownik ze struktur MF).
 * Konto wskazuje pozycję kodem; pozycje `assignable: false` (formuły, nagłówki) nie do wyboru.
 */
export type StatementPosition = {
  code: string;
  parentCode: string | null;
  section: 'ASSETS' | 'LIABILITIES' | 'INCOME';
  element: string;
  label: string;
  level: number;
  informational: boolean;
  naturalSide: AccountSide | null;
  formula: string | null;
  special: string | null;
  assignable: boolean;
};

/** Dziennik: zapisy dwustronne (E7-S3). Kwota zawsze dodatnia — stronę niesie `side`. */
export type EntrySide = 'DEBIT' | 'CREDIT';
export type JournalSource = 'INVOICE' | 'COST' | 'CORRECTION' | 'DEPRECIATION' | 'MANUAL';

export type JournalLine = {
  lineNo: number;
  account: string;
  accountName: string;
  side: EntrySide;
  amount: string;
  currency: string;
  amountCurrency: string | null;
  counterpartyId: string | null;
  counterpartyName: string | null;
  description: string | null;
  /** Pozycja rozrachunkowa, której wiersz dotyczy — tylko na kontach rozrachunkowych (E7-S4). */
  settlementId: string | null;
};

export type JournalEntry = {
  id: string;
  entryNo: number;
  period: string;
  postingDate: string;
  description: string;
  sourceType: JournalSource | null;
  sourceId: string | null;
  /** Wypełnione, gdy ten zapis jest stornem innego — oryginał zostaje nietknięty. */
  reversalOfEntryId: string | null;
  openingBalance: boolean;
  totalDebit: string;
  totalCredit: string;
  createdAt: string;
  lines: JournalLine[];
};

/** Dokument czekający na zaksięgowanie razem z dekretem ułożonym przez schemat (E7-S2). */
export type PostingProposal = {
  documentId: string;
  kind: 'INVOICE' | 'CORRECTION' | 'COST';
  number: string;
  date: string;
  counterpartyName: string | null;
  gross: string;
  schemeName: string | null;
  lines: {
    account: string;
    accountName: string;
    side: EntrySide;
    amount: string;
    counterpartyId: string | null;
  }[];
  /** Czego brakuje; dokument z powodem zostaje na liście, zamiast zniknąć bez słowa. */
  problem: string | null;
  postable: boolean;
};

export type NewJournalLine = {
  account: string;
  side: EntrySide;
  amount: string;
  counterpartyId?: string | null;
  description?: string | null;
  /** Rozliczana pozycja; brak przy koncie rozrachunkowym znaczy: nowa pozycja. */
  settlementId?: string | null;
};

// MARK: - Rozrachunki (E7-S4)

export type AgingBucket = 'NOT_DUE' | 'DAYS_1_30' | 'DAYS_31_60' | 'DAYS_61_90' | 'OVER_90';

/** Pozycja rozrachunkowa: saldo liczone z wierszy dziennika, wiekowanie po terminie płatności. */
export type OpenItem = {
  id: string;
  account: string;
  counterpartyId: string;
  counterpartyName: string | null;
  /** DEBIT — należność, CREDIT — zobowiązanie. */
  side: EntrySide;
  sourceType: JournalSource | null;
  sourceId: string | null;
  documentNumber: string | null;
  documentDate: string;
  dueDate: string | null;
  currency: string;
  amount: string;
  settled: string;
  remaining: string;
  /** Ile zostało w walucie dokumentu; przy pozycji złotowej zero. */
  remainingCurrency: string;
  daysOverdue: number;
  bucket: AgingBucket;
};

/** Pozycja do wyceny walut: rachunek walutowy albo otwarty rozrachunek w walucie (E7-S5). */
export type FxRevaluationItem = {
  kind: 'ACCOUNT' | 'SETTLEMENT';
  account: string;
  label: string;
  currency: string;
  /** Saldo w walucie i w złotych, dodatnie po stronie Wn. */
  balanceCurrency: string;
  balance: string;
  rate: string | null;
  revalued: string | null;
  difference: string | null;
};

export type FxRevaluation = {
  asOf: string;
  items: FxRevaluationItem[];
  gain: string;
  loss: string;
  /** Waluty bez podanego kursu — bez nich nie ma czego księgować. */
  missingRates: string[];
  /** Numer zapisu: wyceny właśnie zaksięgowanej albo tej, która na ten dzień już była. */
  entryNo: number | null;
};

/**
 * Ulga na złe długi — w VAT (art. 89a i 89b ustawy o VAT) i w podatku dochodowym (art. 18f ustawy
 * o CIT, art. 26i ustawy o PIT). Reguły są te same, różnią się kwoty: w VAT koryguje się podatek,
 * w dochodowym — kwotę netto.
 */
export type BadDebtSide = 'PURCHASE' | 'SALE';
export type BadDebtKind = 'DECREASE' | 'INCREASE' | 'SKIPPED';
export type BadDebtTax = 'VAT' | 'INCOME';

export type BadDebtCandidate = {
  side: BadDebtSide;
  tax: BadDebtTax;
  documentId: string;
  documentNumber: string;
  counterpartyName: string | null;
  counterpartyNip: string | null;
  issueDate: string;
  dueDate: string;
  /** Dzień, w którym mija dziewięćdziesiąty dzień od terminu płatności. */
  ninetiethDay: string;
  /** Okres, do którego korekta należy — nie zawsze ten, w którym się ją zauważyło. */
  period: string;
  net: string;
  vat: string;
  unpaid: string;
  /** Korekta nabywcy jest obowiązkiem, ulga sprzedawcy — prawem. */
  mandatory: boolean;
  /** Dziewięćdziesiąty dzień minął przed tym okresem: potrzebna korekta deklaracji. */
  late: boolean;
  paymentId: string | null;
  paidOn: string | null;
};

export type BadDebtCorrection = {
  id: string;
  side: BadDebtSide;
  tax: BadDebtTax;
  kind: BadDebtKind;
  period: string;
  documentId: string;
  documentNumber: string;
  counterpartyName: string | null;
  net: string;
  vat: string;
  reason: string | null;
};

export type BadDebtReview = {
  period: string;
  tax: BadDebtTax;
  purchases: BadDebtCandidate[];
  sales: BadDebtCandidate[];
  restorations: BadDebtCandidate[];
  recorded: BadDebtCorrection[];
};

export type BadDebtDecision = {
  side: BadDebtSide;
  documentId: string;
  correct: boolean;
  reason?: string | null;
  paymentId?: string | null;
};

/** Informacja podsumowująca VAT-UE (E8-S4). */
export type VatUeRow = {
  counterpartyName: string | null;
  /** Kod kraju ze schemy MF — Grecja to EL, Irlandia Północna XI. */
  countryCode: string;
  /** Numer VAT bez prefiksu kraju. */
  vatNumber: string;
  /** Kwota w pełnych złotych. */
  amount: string;
};

export type VatUeProblem = { documentNumber: string | null; counterpartyName: string | null; reason: string };

export type VatUeNote =
  | 'TRIANGULAR_NOT_DETECTED'
  | 'VIES_ON_DEMAND'
  | 'CALL_OFF_NOT_SUPPORTED'
  | 'MONTHLY_ALWAYS';

export type VatUeSummary = {
  period: string;
  /** Część C — dostawy towarów. */
  supplies: VatUeRow[];
  /** Część D — nabycia towarów. */
  acquisitions: VatUeRow[];
  /** Część E — świadczenie usług. */
  services: VatUeRow[];
  problems: VatUeProblem[];
  notes: VatUeNote[];
  /** Bez transakcji nie ma obowiązku — informacji zerowej się nie składa. */
  empty: boolean;
};

/** Sprawdzenie numeru VAT-UE w VIES razem z datą — dowód należytej staranności (E8-S4). */
export type ViesCheck = {
  id: string;
  counterpartyId: string;
  counterpartyName: string | null;
  countryCode: string;
  vatNumber: string;
  valid: boolean;
  name: string | null;
  address: string | null;
  /** Numer referencyjny VIES; pusty przy sprawdzeniu anonimowym. */
  requestIdentifier: string | null;
  checkedAt: string;
};

/** Wynik sprawdzenia wielu numerów: co sprawdzono i czego rejestr nie zdążył obsłużyć. */
export type ViesBatch = { checked: ViesCheck[]; unavailable: string[] };

/** Roczne rozliczenie podatku dochodowego osoby fizycznej (E9-S1). */
export type PitForm = 'PIT_36' | 'PIT_36L' | 'PIT_28';

export type PitAnnualNote =
  | 'ESTIMATE'
  | 'ADVANCES_ASSUMED_PAID'
  | 'BUSINESS_INCOME_ONLY'
  | 'NO_RELIEFS'
  | 'LOSS_FROM_USER'
  | 'HEALTH_NOT_DEDUCTIBLE_ON_SCALE'
  | 'RYCZALT_HEALTH_HALF'
  | 'INVENTORY_MISSING'
  | 'FULL_BOOKS_NOT_SUPPORTED'
  | 'PARTNERS_SPLIT'
  | 'UNKNOWN_YEAR'
  | 'BAD_DEBT_IN_INCOME'
  | 'BAD_DEBT_NOT_IN_RYCZALT';

export type PitAnnual = {
  year: number;
  form: PitForm;
  taxpayer: string | null;
  partnerId: string | null;
  share: string;
  revenue: string;
  income: string;
  /** Ile z dochodu bierze się z korekt złych długów; dodatnia podnosi, ujemna obniża. */
  badDebtAdjustment: string;
  socialPaid: string;
  healthPaid: string;
  deducted: string;
  priorYearLoss: string;
  taxBase: string;
  tax: string;
  advancesPaid: string;
  /** Dodatnia kwota to dopłata, ujemna — nadpłata do zwrotu. */
  difference: string;
  notes: PitAnnualNote[];
};

/**
 * Roczne rozliczenie składki zdrowotnej (art. 81 ustawy o świadczeniach opieki zdrowotnej).
 *
 * `basis` × `rate` zawsze daje `due` — także wtedy, gdy zadziałała składka minimalna, bo wtedy
 * podstawą jest minimalne wynagrodzenie, a stawką 9% (również u liniowca). Inaczej ekran
 * pokazywałby trzy liczby, z których dwie się nie mnożą.
 */
export type HealthAnnualNote =
  | 'ESTIMATE'
  | 'MONTHLY_ASSUMED_PAID'
  | 'BUSINESS_ONLY'
  | 'MINIMUM_APPLIED'
  | 'RYCZALT_BRACKET_FROM_REVENUE'
  | 'SOCIAL_NOT_DEDUCTED'
  | 'BAD_DEBT_NOT_IN_BASIS'
  | 'INVENTORY_MISSING'
  | 'FULL_BOOKS_NOT_SUPPORTED'
  | 'FORM_CHANGED'
  | 'PARTNERS_SPLIT'
  | 'UNKNOWN_YEAR';

export type HealthAnnual = {
  year: number;
  taxpayer: string | null;
  partnerId: string | null;
  taxationForm: TaxationForm;
  share: string;
  /** Miesiące podlegania ubezpieczeniu zdrowotnemu — z nich liczy się minimum i próg ryczałtu. */
  months: number;
  income: string;
  revenue: string;
  basis: string;
  rate: string;
  due: string;
  paid: string;
  /** Dodatnia kwota to dopłata, ujemna — nadpłata do zwrotu. */
  difference: string;
  notes: HealthAnnualNote[];
};

export type Aging = { total: string; byBucket: Record<AgingBucket, string> };

export type OpenItemsReport = { asOf: string; items: OpenItem[]; receivables: Aging; payables: Aging };

export type OffsetRequest = { receivableId: string; payableId: string; amount: string; date: string };

/** Bilans otwarcia z poprzedniego roku — co przejdzie, zanim cokolwiek trafi do ksiąg. */
export type OpeningLine = {
  account: string;
  accountName: string;
  side: EntrySide;
  amount: string;
  counterpartyId: string | null;
  counterpartyName: string | null;
  documentNumber: string | null;
  dueDate: string | null;
};

export type OpeningBalancePreview = {
  previousYearStartsOn: string;
  previousYearEndsOn: string;
  lines: OpeningLine[];
  totalDebit: string;
  totalCredit: string;
  result: string;
  existingEntryId: string | null;
};

export type NewJournalEntry = {
  postingDate: string;
  description: string;
  lines: NewJournalLine[];
  openingBalance?: boolean;
};

export type TrialBalanceRow = {
  account: string;
  name: string;
  kind: AccountKind;
  side: AccountSide;
  openingDebit: string;
  openingCredit: string;
  turnoverDebit: string;
  turnoverCredit: string;
  closingDebit: string;
  closingCredit: string;
};

export type TrialBalance = {
  rows: TrialBalanceRow[];
  openingDebit: string;
  openingCredit: string;
  turnoverDebit: string;
  turnoverCredit: string;
  closingDebit: string;
  closingCredit: string;
  /** Trzy równości naraz; fałsz znaczy, że księgi nie są gotowe do sprawozdania. */
  balanced: boolean;
};

/** Wiersz sprawozdania: konto, jego zespół i kwota po tej stronie, na której konto ma saldo. */
export type StatementLine = {
  account: string;
  name: string;
  group: number;
  amount: string;
  /** Ta sama pozycja rok wcześniej; null, gdy nie ma poprzedniego roku albo konto nie ma odpowiednika. */
  previousAmount: string | null;
};

export type BalanceSheet = {
  assets: StatementLine[];
  liabilities: StatementLine[];
  totalAssets: string;
  totalLiabilities: string;
  /** Wynik roku; dodatni to zysk. Stoi po stronie pasywów i to on domyka równanie bilansu. */
  result: string;
  balanced: boolean;
};

export type IncomeStatement = {
  revenues: StatementLine[];
  costs: StatementLine[];
  totalRevenue: string;
  totalCost: string;
  result: string;
};

export type StatutoryRow = {
  code: string;
  parentCode: string | null;
  element: string;
  label: string;
  level: number;
  informational: boolean;
  computed: boolean;
  header: boolean;
  amount: string | null;
  previousAmount: string | null;
};

/** Konto z saldem bez pozycji — jego kwoty w sprawozdaniu nie ma. */
export type UnmappedAccount = { account: string; name: string; side: EntrySide; amount: string };

export type StatutoryStatement = {
  template: StatementTemplate;
  costVariant: CostVariant;
  assets: StatutoryRow[];
  liabilities: StatutoryRow[];
  income: StatutoryRow[];
  totalAssets: string;
  totalLiabilities: string;
  result: string;
  balanced: boolean;
  unmapped: UnmappedAccount[];
  previousTotalAssets: string | null;
  previousTotalLiabilities: string | null;
  previousResult: string | null;
};

/** Poprzedni rok obrotowy — kolumna porównawcza. */
export type PreviousYear = { startsOn: string; endsOn: string; hasChart: boolean; comparable: boolean };

export type PreviousTotals = { assets: string; liabilities: string; revenue: string; cost: string; result: string };

export type Statements = {
  balanceSheet: BalanceSheet;
  incomeStatement: IncomeStatement;
  /** Null, gdy rok nie ma planu kont. */
  statutory: StatutoryStatement | null;
  previous: PreviousYear | null;
  previousTotals: PreviousTotals | null;
};

/** Dokument w archiwum: oryginał zdjęcia albo PDF-a (E5-S2). */
export type DocumentStatus = 'NEW' | 'IN_PROGRESS' | 'BOOKED' | 'REJECTED';
export type DocumentSource = 'UPLOAD' | 'EMAIL';

/** Adres, na który firma wysyła dokumenty pocztą; kilka na firmę, każdy z etykietą (E17-S2). */
export type IntakeAddress = { id: string; address: string; tag: string | null; createdAt: string; revokedAt: string | null; active: boolean };
export type IntakeSender = { id: string; email: string; createdAt: string };

export type ArchivedDocument = {
  id: string;
  kind: 'COST_SCAN' | 'INVOICE_ATTACHMENT' | 'HANDOVER' | 'OTHER';
  status: DocumentStatus;
  rejectionReason: string | null;
  /** Etykieta z adresu wpływu (faktury, wyciągi…). */
  tag: string | null;
  source: DocumentSource;
  receivedFrom: string | null;
  note: string | null;
  fileName: string;
  contentType: string;
  byteSize: number;
  sha256: string;
  createdAt: string;
  costDocumentId: string | null;
  /** Prawda, gdy ten sam plik był już w archiwum i serwer oddał wpis sprzed chwili. */
  alreadyExisted: boolean;
};

/* ── Sprawozdanie roczne do KRS (docs/08) ───────────────────────────────────────────────────── */

/**
 * Stan sprawozdania. Kody idą małymi literami, tak jak w API — ekran nie tłumaczy ich sam,
 * bo lista stanów jest częścią umowy z serwerem, a nie ozdobą.
 */
export type ReportStatus =
  | 'draft'
  | 'assembled'
  | 'signing'
  | 'signed'
  | 'approved'
  | 'filed'
  | 'filed_unapproved'
  | 'confirmed'
  | 'abandoned';

export type ReportFileRole =
  | 'statement_xml'
  | 'statement_xml_signed'
  | 'statement_pdf'
  | 'activity_report'
  | 'resolution_approval'
  | 'resolution_profit'
  | 'resolution_discharge'
  | 'minutes'
  | 'statement_52_2b'
  | 'auditor_report'
  | 'rdf_confirmation';

export type AnnualReportFile = {
  id: string;
  role: ReportFileRole;
  documentId: string;
  sha256: string;
  generatedAt: string;
};

/** Skąd biorą się kwoty sprawozdania (docs/08, ścieżka skrócona). */
export type ReportSource = 'BOOKS' | 'DORMANT' | 'MANUAL';

export type AnnualReport = {
  id: string;
  fiscalYearId: string;
  status: ReportStatus;
  version: number;
  supersedesId: string | null;
  versionReason: string | null;
  source: 'books' | 'dormant' | 'manual';
  statementTemplate: StatementTemplate;
  costVariant: CostVariant;
  presentation: string;
  /** Znak „wersja robocza” (docs/01 §6.2) — dokument nosi go w treści, ekran musi o tym mówić. */
  draftMark: boolean;
  preparedOn: string | null;
  approvedOn: string | null;
  filedOn: string | null;
  confirmedOn: string | null;
  /** Księgi zmieniły się po wygenerowaniu pliku; stanu to nie zmienia, ale trzeba to wiedzieć. */
  stale: boolean;
  files: AnnualReportFile[];
};

/** Pozycja sprawozdania na ekranie kwot (tryby `dormant` i `manual`). */
export type ManualPosition = {
  section: 'assets' | 'liabilities' | 'income';
  code: string;
  label: string;
  level: number;
  /** Pozycja „w tym” — podzbiór rodzica, więc nie sumuje się do niego. */
  informational: boolean;
  computed: boolean;
  editable: boolean;
  amount: string;
  previousAmount: string | null;
};

export type ManualStatementView = {
  source: string;
  template: StatementTemplate;
  costVariant: CostVariant;
  positions: ManualPosition[];
  totalAssets: string;
  totalLiabilities: string;
  result: string;
  /** Aktywa minus pasywa; zero znaczy, że bilans się domyka. */
  difference: string;
  balanced: boolean;
  notes: string[];
};

export type AnswerSection = 'INTRODUCTION' | 'NOTES' | 'SEAT' | 'TAX';

/** Pole ze wzoru sprawozdania: kod to nazwa elementu ze schemy, nie nasza wymyślona nazwa. */
export type IntroField = {
  code: string;
  parentCode: string | null;
  label: string;
  kind: string;
  required: boolean;
  ordinal: number;
};

export type IntroAnswer = { section: string; code: string; value: string; source: string };

export type IntroForm = {
  fields: IntroField[];
  answers: IntroAnswer[];
  /** Czego brakuje do złożenia pliku — liczone przez serwer ze schemy, nie zgadywane na ekranie. */
  missing: { section: string; code: string; label: string }[];
  seatCodes: string[];
  taxCodes: string[];
  complete: boolean;
};

/** Sekcja sprawozdania zarządu razem z tym, skąd jest jej treść. */
export type ActivitySectionView = {
  code: string;
  title: string;
  legalBasis: string | null;
  required: boolean;
  /** Treść składa aplikacja z danych; człowiek dopisuje najwyżej komentarz. */
  fromData: boolean;
  text: string | null;
  /** `generated` znaczy „propozycja" — taki akapit nie wchodzi do dokumentu. */
  source: string | null;
};

export type ActivityIndicator = {
  code: string;
  label: string;
  /** Puste znaczy „wskaźnik nie ma sensu"; wtedy `explanation` mówi dlaczego. */
  value: string | null;
  previousValue: string | null;
  basis: string;
  explanation: string | null;
};

export type ActivitySuggestion = { code: string; section: string; text: string; basis: string };

export type ActivityReportView = {
  reportId: string;
  sections: ActivitySectionView[];
  indicators: ActivityIndicator[];
  suggestions: ActivitySuggestion[];
  signers: string[];
  missing: string[];
  ready: boolean;
  notes: string[];
};

export type SignatureCapacity = 'BOARD_MEMBER' | 'BOOKKEEPER';
export type SignatureMethod = 'QUALIFIED' | 'TRUSTED_PROFILE' | 'PERSONAL' | 'STATEMENT' | 'REFUSAL';

export type SignatureView = {
  id: string;
  capacity: string;
  method: string;
  personName: string;
  officerId: string | null;
  signedAt: string;
  signedSha256: string | null;
  matchesStatement: boolean;
  /** Podpis przeszedł sprawdzenie XMLDSig — **nie** to samo co „certyfikat jest zaufany”. */
  cryptoVerified: boolean;
  certificateSubject: string | null;
  note: string | null;
  documentId: string | null;
};

export type RequiredSigner = {
  capacity: string;
  officerId: string | null;
  name: string;
  role: string | null;
  state: 'missing' | 'signed' | 'statement' | 'refusal';
  method: string | null;
  signedAt: string | null;
};

export type SignaturePanel = {
  reportId: string;
  status: ReportStatus;
  statementFileId: string | null;
  statementSha256: string | null;
  fileToSign: ReportFileRole | null;
  required: RequiredSigner[];
  signatures: SignatureView[];
  /** Podpisy osób spoza zarządu — błąd formalny, nie techniczny. */
  extra: SignatureView[];
  complete: boolean;
  deadline: string | null;
  daysLeft: number | null;
  notes: string[];
};

export type GeneratedDeclaration = { documentId: string; fileName: string; sha256: string };

export type ResolutionOrgan = 'BOARD' | 'SHAREHOLDERS';
export type ResolutionKind =
  | 'APPROVE_STATEMENT'
  | 'APPROVE_ACTIVITY_REPORT'
  | 'PROFIT_DISTRIBUTION'
  | 'LOSS_COVERAGE'
  | 'CONTINUE_DESPITE_LOSS'
  | 'DISCHARGE'
  | 'CONVENE_ORDINARY_MEETING'
  | 'SIMPLIFICATIONS'
  | 'BOARD_STATEMENT_PREPARED'
  | 'BOARD_CONVENE_MEETING'
  | 'OTHER';

export type Resolution = {
  id: string;
  organ: string;
  kind: string;
  number: string | null;
  adoptedOn: string | null;
  title: string;
  body: string;
  status: 'draft' | 'adopted' | 'cancelled';
  source: string;
  fiscalYearId: string | null;
  annualReportId: string | null;
  documentId: string | null;
  createdAt: string;
};

/** Uchwała do podjęcia razem z kwotami wyliczonymi z ksiąg — ekran ich nie liczy. */
export type ResolutionProposal = {
  kind: ResolutionKind;
  organ: ResolutionOrgan;
  title: string;
  reason: string;
  params: Record<string, string>;
  existingId: string | null;
  existingStatus: string | null;
};

export type ResolutionPlan = { proposals: ResolutionProposal[]; gaps: string[] };

export type PackageItem = {
  role: string;
  label: string;
  present: boolean;
  required: boolean;
  note: string | null;
};

export type Filing = {
  id: string;
  reportId: string;
  kind: 'RDF_FREE' | 'S24_PAID';
  submittedOn: string;
  submissionNumber: string | null;
  unapproved: boolean;
  confirmationDocumentId: string | null;
  confirmedOn: string | null;
};

export type RdfPackage = {
  reportId: string;
  status: ReportStatus;
  items: PackageItem[];
  ready: boolean;
  people: { name: string; role: string; peselInKrs: boolean }[];
  instructions: string[];
  deadlines: Record<string, string | null>;
  filings: Filing[];
  notes: string[];
};

export type AnnualObligationKind =
  | 'statement_prepared'
  | 'cit8'
  | 'approval'
  | 'krs_filing'
  | 'krs_confirmation';

export type AnnualObligation = {
  kind: AnnualObligationKind;
  fiscalYearId: string;
  fiscalYearLabel: string;
  /** Termin z ustawy. */
  statutoryDate: string;
  /** Ostatni dzień roboczy nie później niż termin ustawowy — bo w sobotę nie ma komu złożyć. */
  safeDate: string;
  done: boolean;
  doneOn: string | null;
  daysLeft: number | null;
  overdue: boolean;
  note: string | null;
};

export type OfficerRole = 'PRESIDENT' | 'BOARD_MEMBER' | 'PROXY' | 'LIQUIDATOR';

export type Officer = {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  roleLabel: string;
  fromDate: string;
  toDate: string | null;
  /** Numer PESEL ujawniony w KRS — od tego zależy, czy bezpłatne zgłoszenie do RDF ma kto podpisać. */
  peselInKrs: boolean;
};

export type Shareholder = {
  id: string;
  name: string;
  shares: number;
  votes: number;
  nominalValue: string;
  capital: string;
  fromDate: string;
  toDate: string | null;
};

export type KrsMention = {
  periodFrom: string;
  periodTo: string;
  statementFiledOn: string | null;
  hasApprovalResolution: boolean;
  hasActivityReport: boolean;
  hasAuditReport: boolean;
};

export type RegisterStatus = {
  krs: string | null;
  checkedAt: string | null;
  mentions: KrsMention[];
  /** Lata bez wzmianki — zaległości, o których klient może nie wiedzieć. */
  missingYears: string[];
  confirmed: string[];
};

export type ClientAnnualStatus = {
  companyId: string;
  companyName: string;
  companyNip: string;
  krs: string | null;
  fiscalYearId: string | null;
  fiscalYearEndsOn: string | null;
  status: ReportStatus | null;
  version: number | null;
  preparedOn: string | null;
  approvedOn: string | null;
  filedOn: string | null;
  confirmedOn: string | null;
  mentionInRegister: boolean;
};

export type OfficeAnnualPanel = { year: number; clients: ClientAnnualStatus[] };

/** Błąd z gotowym zdaniem dla użytkownika — API opisuje błędy zgodnie z RFC 9457. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly status?: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}, anonymous = false): Promise<T> {
  const user = anonymous ? null : await userManager.getUser();
  if (!anonymous && (!user || user.expired)) {
    throw new ApiError('Sesja wygasła — zaloguj się ponownie.', 'unauthorized', 401);
  }

  let response: Response;
  try {
    response = await fetch(`${config.apiBaseUrl}/${path}`, {
      ...init,
      headers: {
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(user ? { Authorization: `Bearer ${user.access_token}` } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError('Nie udało się połączyć z serwerem.', 'network');
  }

  if (response.status === 401) throw new ApiError('Sesja wygasła — zaloguj się ponownie.', 'unauthorized', 401);
  if (!response.ok) {
    const problem = await response.json().catch(() => null);
    throw new ApiError(
      problem?.detail ?? `Serwer odpowiedział: ${response.status}.`,
      problem?.code,
      response.status,
    );
  }
  // Nie każda udana odpowiedź ma treść: rejestracja kończy się kodem 201 z pustym ciałem,
  // usunięcie 204. Wywołanie `json()` na pustej odpowiedzi rzuca „Unexpected end of JSON input",
  // czyli błąd przeglądarki pokazywany w miejscu, gdzie wszystko właśnie się udało
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * Wysłanie pliku.
 *
 * Nie korzysta z `request`, bo przy formularzu wieloczęściowym nagłówka `Content-Type`
 * **nie wolno ustawiać samemu** — przeglądarka dopisuje do niego granicę części i bez niej
 * serwer nie rozdzieli pliku od reszty żądania.
 */
async function upload<T>(path: string, file: File): Promise<T> {
  const user = await userManager.getUser();
  if (!user || user.expired) {
    throw new ApiError('Sesja wygasła — zaloguj się ponownie.', 'unauthorized', 401);
  }

  const body = new FormData();
  body.append('file', file);

  let response: Response;
  try {
    response = await fetch(`${config.apiBaseUrl}/${path}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${user.access_token}` },
      body,
    });
  } catch {
    throw new ApiError('Nie udało się połączyć z serwerem.', 'network');
  }

  if (!response.ok) {
    const problem = await response.json().catch(() => null);
    throw new ApiError(
      problem?.detail ?? `Serwer odpowiedział: ${response.status}.`,
      problem?.code,
      response.status,
    );
  }
  return (await response.json()) as T;
}

/** Plik do zapisania na dysku: treść i nazwa zaproponowana przez serwer. */
export type DownloadedFile = { blob: Blob; fileName: string };

/** Wysyłka JPK do bramki Ministerstwa Finansów i jej stan; UPO, gdy urząd przyjął plik. */
export type JpkSubmission = {
  id: string;
  period: string;
  fileName: string;
  authMode: 'AUTHORIZATION_DATA' | 'SIGNATURE';
  status: 'AWAITING_SIGNATURE' | 'SENDING' | 'PROCESSING' | 'ACCEPTED' | 'REJECTED' | 'FAILED';
  referenceNumber: string | null;
  statusCode: number | null;
  statusDescription: string | null;
  hasUpo: boolean;
  environment: 'test' | 'production';
  createdAt: string;
};

/**
 * Pobranie pliku. Różni się od `request` dwiema rzeczami: odpowiedzi nie czytamy jako JSON,
 * a nazwę pliku bierzemy z nagłówka od serwera — to on wie, jak plik ma się nazywać.
 */
async function download(path: string): Promise<DownloadedFile> {
  const user = await userManager.getUser();
  if (!user || user.expired) {
    throw new ApiError('Sesja wygasła — zaloguj się ponownie.', 'unauthorized', 401);
  }

  let response: Response;
  try {
    response = await fetch(`${config.apiBaseUrl}/${path}`, {
      headers: { Authorization: `Bearer ${user.access_token}` },
    });
  } catch {
    throw new ApiError('Nie udało się połączyć z serwerem.', 'network');
  }

  if (!response.ok) {
    const problem = await response.json().catch(() => null);
    throw new ApiError(
      problem?.detail ?? `Serwer odpowiedział: ${response.status}.`,
      problem?.code,
      response.status,
    );
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'plik.xml';
  return { blob: await response.blob(), fileName };
}

/** Zapisanie pobranego pliku na dysku użytkownika. */
export function saveFile({ blob, fileName }: DownloadedFile): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

export const api = {
  me: () => request<Account>('api/me'),
  /** Cennik przed założeniem konta — ceny liczy ten sam cennik, z którego bierze się opłaty. */
  pricing: () => request<Pricing>('api/pricing', {}, true),
  /**
   * Założenie konta. Hasła nie przyjmuje nasza aplikacja — idzie prosto do serwera,
   * a ten zakłada konto w Keycloaku. Zalogowanie jest osobnym krokiem, przez Keycloaka.
   */
  register: (registration: Registration) =>
    request<void>('api/registrations', { method: 'POST', body: JSON.stringify(registration) }, true),
  verifyEmail: (email: string, code: string) =>
    request<void>('api/registrations/verify', { method: 'POST', body: JSON.stringify({ email, code }) }, true),
  resendVerification: (email: string, uiLocale: string) =>
    request<void>(
      'api/registrations/verify/resend',
      { method: 'POST', body: JSON.stringify({ email, uiLocale }) },
      true,
    ),
  companies: () => request<Company[]>('api/companies'),
  createCompany: (company: NewCompany) =>
    request<Company>('api/companies', { method: 'POST', body: JSON.stringify(company) }),
  members: (companyId: string) => request<Member[]>(`api/companies/${companyId}/members`),
  /**
   * Nadanie roli osobie, która już ma konto — używamy tego tam, gdzie właściciel bierze rolę
   * Księgowa dla samego siebie. Kogoś z zewnątrz zaprasza się adresem e-mail, nie identyfikatorem.
   */
  addMember: (companyId: string, userId: string, role: CompanyRole) =>
    request<{ membershipId: string }>(`api/companies/${companyId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId, role }),
    }),
  removeMember: (companyId: string, membershipId: string) =>
    request<void>(`api/companies/${companyId}/members/${membershipId}`, { method: 'DELETE' }),
  invitations: (companyId: string) => request<Invitation[]>(`api/companies/${companyId}/invitations`),
  invite: (
    companyId: string,
    email: string,
    role: CompanyRole,
    uiLocale: string,
    ownDocumentsOnly = false,
  ) =>
    request<Invitation>(`api/companies/${companyId}/invitations`, {
      method: 'POST',
      body: JSON.stringify({ email, role, uiLocale, ownDocumentsOnly }),
    }),
  revokeInvitation: (companyId: string, invitationId: string) =>
    request<void>(`api/companies/${companyId}/invitations/${invitationId}`, { method: 'DELETE' }),
  // Podgląd zaproszenia jest dostępny bez logowania — zapraszany może dopiero zakładać konto
  /** Dane firmy po NIP-ie z wykazu podatników VAT (E1-S1). */
  lookupCompany: (nip: string) => request<CompanyFromRegistry>(`api/lookup/company/${nip.replace(/[^0-9]/g, '')}`),

  /** Zamykanie okresów i rok obrotowy (E7-S6). */
  closedPeriods: (companyId: string) => request<ClosedPeriod[]>(`api/companies/${companyId}/periods`),
  closingCheck: (companyId: string, period: string) =>
    request<ClosingCheck>(`api/companies/${companyId}/periods/check?period=${period}`),
  closePeriod: (companyId: string, period: string, reason?: string) =>
    request<void>(`api/companies/${companyId}/periods`, {
      method: 'POST',
      body: JSON.stringify({ period, reason }),
    }),
  reopenPeriod: (companyId: string, period: string, reason: string) =>
    request<void>(`api/companies/${companyId}/periods/${period}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),
  fiscalYears: (companyId: string) => request<FiscalYear[]>(`api/companies/${companyId}/fiscal-years`),
  chart: (companyId: string, fiscalYearId: string) =>
    request<Chart | null>(`api/companies/${companyId}/fiscal-years/${fiscalYearId}/chart`),
  journal: (companyId: string, fiscalYearId: string, period?: string) =>
    request<JournalEntry[]>(
      `api/companies/${companyId}/fiscal-years/${fiscalYearId}/journal${period ? `?period=${period}` : ''}`,
    ),
  openItems: (companyId: string, fiscalYearId: string, asOf?: string, open = true) =>
    request<OpenItemsReport>(
      `api/companies/${companyId}/fiscal-years/${fiscalYearId}/settlements?open=${open}${asOf ? `&asOf=${asOf}` : ''}`,
    ),
  openingBalancePreview: (companyId: string, fiscalYearId: string) =>
    request<OpeningBalancePreview>(`api/companies/${companyId}/fiscal-years/${fiscalYearId}/journal/opening-balance`),
  recordOpeningBalance: (companyId: string, fiscalYearId: string, replace: boolean) =>
    request<JournalEntry>(`api/companies/${companyId}/fiscal-years/${fiscalYearId}/journal/opening-balance?replace=${replace}`, {
      method: 'POST',
    }),
  offsetItems: (companyId: string, fiscalYearId: string, offset: OffsetRequest) =>
    request<JournalEntry>(`api/companies/${companyId}/fiscal-years/${fiscalYearId}/settlements/offset`, {
      method: 'POST',
      body: JSON.stringify(offset),
    }),
  pendingPostings: (companyId: string, fiscalYearId: string) =>
    request<PostingProposal[]>(`api/companies/${companyId}/fiscal-years/${fiscalYearId}/posting/pending`),
  postDocuments: (companyId: string, fiscalYearId: string, documentIds: string[]) =>
    request<{ posted: JournalEntry[] }>(`api/companies/${companyId}/fiscal-years/${fiscalYearId}/posting`, {
      method: 'POST',
      body: JSON.stringify({ documentIds }),
    }),
  recordEntry: (companyId: string, fiscalYearId: string, entry: NewJournalEntry) =>
    request<JournalEntry>(`api/companies/${companyId}/fiscal-years/${fiscalYearId}/journal`, {
      method: 'POST',
      body: JSON.stringify(entry),
    }),
  reverseEntry: (companyId: string, fiscalYearId: string, entryId: string, reason: string) =>
    request<JournalEntry>(
      `api/companies/${companyId}/fiscal-years/${fiscalYearId}/journal/${entryId}/reversal`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),
  accountEntries: (companyId: string, fiscalYearId: string, account: string, from?: string, to?: string) =>
    request<JournalEntry[]>(
      `api/companies/${companyId}/fiscal-years/${fiscalYearId}/journal/accounts/${account}` +
        `?from=${from ?? ''}&to=${to ?? ''}`,
    ),
  trialBalance: (companyId: string, fiscalYearId: string, upTo?: string) =>
    request<TrialBalance>(
      `api/companies/${companyId}/fiscal-years/${fiscalYearId}/journal/trial-balance${upTo ? `?upTo=${upTo}` : ''}`,
    ),
  statements: (companyId: string, fiscalYearId: string, upTo?: string) =>
    request<Statements>(
      `api/companies/${companyId}/fiscal-years/${fiscalYearId}/journal/statements${upTo ? `?upTo=${upTo}` : ''}`,
    ),
  chartPreview: (companyId: string, fiscalYearId: string, source: ChartSource, templateId?: string) =>
    request<ChartPreviewAccount[]>(
      `api/companies/${companyId}/fiscal-years/${fiscalYearId}/chart/preview?source=${source}` +
        (templateId ? `&templateId=${templateId}` : ''),
    ),
  chartTemplates: (companyId: string) => request<ChartTemplateInfo[]>(`api/companies/${companyId}/chart-templates`),
  statementPositions: (companyId: string, template: StatementTemplate) =>
    request<StatementPosition[]>(`api/companies/${companyId}/statement-positions?template=${template}`),
  accountTags: (companyId: string) => request<AccountTagInfo[]>(`api/companies/${companyId}/account-tags`),
  createChart: (
    companyId: string,
    fiscalYearId: string,
    body: { source: ChartSource; templateId?: string; statementTemplate?: StatementTemplate; costVariant?: CostVariant },
  ) =>
    request<Chart>(`api/companies/${companyId}/fiscal-years/${fiscalYearId}/chart`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteChart: (companyId: string, fiscalYearId: string) =>
    request<void>(`api/companies/${companyId}/fiscal-years/${fiscalYearId}/chart`, { method: 'DELETE' }),
  addAccount: (companyId: string, fiscalYearId: string, account: NewAccount) =>
    request<ChartAccount>(`api/companies/${companyId}/fiscal-years/${fiscalYearId}/chart/accounts`, {
      method: 'POST',
      body: JSON.stringify(account),
    }),
  updateAccount: (companyId: string, fiscalYearId: string, id: string, account: NewAccount) =>
    request<ChartAccount>(`api/companies/${companyId}/fiscal-years/${fiscalYearId}/chart/accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(account),
    }),
  deleteAccount: (companyId: string, fiscalYearId: string, id: string) =>
    request<void>(`api/companies/${companyId}/fiscal-years/${fiscalYearId}/chart/accounts/${id}`, { method: 'DELETE' }),
  saveChartAsTemplate: (
    companyId: string,
    fiscalYearId: string,
    body: { name: string; description?: string; scope: TemplateScope; officeId?: string },
  ) =>
    request<ChartTemplateInfo>(`api/companies/${companyId}/fiscal-years/${fiscalYearId}/chart/save-as-template`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /**
   * Otwiera brakujące lata obrotowe aż po ten, w którym stoimy dziś — rytmem poprzedniego roku.
   * Rok obrotowy powstawał dotąd tylko przy zakładaniu firmy, więc rok później nie było na czym
   * księgować.
   */
  openCurrentFiscalYear: (companyId: string) =>
    request<FiscalYear[]>(`api/companies/${companyId}/fiscal-years/current`, { method: 'POST' }),
  defineFiscalYear: (companyId: string, startsOn: string, endsOn: string) =>
    request<FiscalYear>(`api/companies/${companyId}/fiscal-years`, {
      method: 'POST',
      body: JSON.stringify({ startsOn, endsOn }),
    }),
  /** Co stoi na drodze zamknięciu roku — do przeczytania przed, a nie po. */
  yearReadiness: (companyId: string, yearId: string) =>
    request<YearReadiness>(`api/companies/${companyId}/fiscal-years/${yearId}/readiness`),
  /**
   * Zamknięcie roku przechodzi przez listę kontrolną: blokad nie da się przejść, uwagi wymagają
   * jawnej zgody (`acceptWarnings`). Osobnej, szybkiej drogi zamknięcia nie ma celowo.
   */
  closeFiscalYear: (companyId: string, yearId: string, acceptWarnings = false) =>
    request<YearClosing>(`api/companies/${companyId}/fiscal-years/${yearId}/closing`, {
      method: 'POST',
      body: JSON.stringify({ acceptWarnings }),
    }),

  /* ── Sprawozdanie roczne do KRS (docs/08) ─────────────────────────────────────────────────── */

  annualReports: (companyId: string) => request<AnnualReport[]>(`api/companies/${companyId}/annual-reports`),
  annualReport: (companyId: string, reportId: string) =>
    request<AnnualReport>(`api/companies/${companyId}/annual-reports/${reportId}`),
  /**
   * Nowe sprawozdanie za rok obrotowy.
   *
   * `source` mówi, skąd wezmą się kwoty: `BOOKS` z ksiąg (domyślnie), `DORMANT` dla spółki bez
   * działalności, `MANUAL` dla ksiąg prowadzonych poza Firmeto. W trybach ręcznych planu kont nie
   * ma skąd wziąć wzoru, więc wybiera go człowiek.
   */
  createAnnualReport: (
    companyId: string,
    fiscalYearId: string,
    options: { source?: ReportSource; statementTemplate?: StatementTemplate; costVariant?: CostVariant } = {},
  ) =>
    request<AnnualReport>(`api/companies/${companyId}/annual-reports`, {
      method: 'POST',
      body: JSON.stringify({ fiscalYearId, ...options }),
    }),
  /**
   * Pusty wzór sprawozdania: wszystkie pozycje załącznika bez kwot.
   *
   * Niczego nie tworzy i nie zmienia — to wydruk do rozmowy z klientem albo do zebrania kwot poza
   * aplikacją. Arkusz wraca od klienta wypełniony i kwoty przepisuje się na ekranie „Kwoty”.
   */
  statementBlank: (
    companyId: string,
    template: StatementTemplate,
    format: 'pdf' | 'xlsx' = 'pdf',
    variant: CostVariant = 'BY_KIND',
  ) =>
    download(
      `api/companies/${companyId}/statement-blank?template=${template}&variant=${variant}&format=${format}`,
    ),
  /** Kartoteka wspólników spółki osobowej (E18-S2). */
  partners: (companyId: string) => request<Partner[]>(`api/companies/${companyId}/partners`),
  addPartner: (
    companyId: string,
    partner: {
      firstName: string;
      lastName: string;
      profitShare: string;
      taxationForm: TaxationForm;
      zusScheme?: ZusScheme | null;
      sicknessInsurance?: boolean;
      fromDate: string;
    },
  ) => request<Partner>(`api/companies/${companyId}/partners`, { method: 'POST', body: JSON.stringify(partner) }),
  endPartner: (companyId: string, id: string, toDate: string) =>
    request<void>(`api/companies/${companyId}/partners/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ toDate }),
    }),

  /** Rozliczenie roczne CIT-8: podatek za rok wobec tego, co pokryły zaliczki (E9-S2). */
  cit8: (companyId: string, fiscalYearId: string, priorYearLoss?: string) =>
    request<CitAnnual>(
      `api/companies/${companyId}/cit8?fiscalYearId=${fiscalYearId}` +
        (priorYearLoss ? `&priorYearLoss=${priorYearLoss}` : ''),
    ),
  /** Kwoty wpisywane ręcznie — tylko w trybach `dormant` i `manual` (docs/08, ścieżka skrócona). */
  manualAmounts: (companyId: string, reportId: string) =>
    request<ManualStatementView>(`api/companies/${companyId}/annual-reports/${reportId}/amounts`),
  saveManualAmounts: (
    companyId: string,
    reportId: string,
    entries: { positionCode: string; amount: string; previousAmount?: string | null }[],
  ) =>
    request<ManualStatementView>(`api/companies/${companyId}/annual-reports/${reportId}/amounts`, {
      method: 'PUT',
      body: JSON.stringify(entries),
    }),
  /** Pola wprowadzenia i informacji dodatkowej ze wzoru, dzisiejsze odpowiedzi i braki. */
  annualForm: (companyId: string, reportId: string) =>
    request<IntroForm>(`api/companies/${companyId}/annual-reports/${reportId}/form`),
  saveAnnualAnswers: (
    companyId: string,
    reportId: string,
    answers: { section: AnswerSection; code: string; value: string }[],
  ) =>
    request<IntroForm>(`api/companies/${companyId}/annual-reports/${reportId}/answers`, {
      method: 'PUT',
      body: JSON.stringify(answers),
    }),
  /**
   * Złożenie pliku sprawozdania z ksiąg i z formularza.
   *
   * Kwot nie przesyłamy — nie ma czym: liczy je projekcja z ksiąg. To jedyny sposób zmiany treści
   * sprawozdania (docs/08 §4), więc ekran nie ma pola na kwotę i nie może go mieć.
   */
  generateStatement: (companyId: string, reportId: string, taxReconciliation = false) =>
    request<AnnualReport>(`api/companies/${companyId}/annual-reports/${reportId}/statement`, {
      method: 'POST',
      body: JSON.stringify({ taxReconciliation }),
    }),
  statementFile: (companyId: string, reportId: string) =>
    download(`api/companies/${companyId}/annual-reports/${reportId}/statement`),
  /**
   * Sprawozdanie do czytania.
   *
   * Osobny dokument od pliku XML: ten idzie do rejestru i pod nim zbiera się podpisy, a wydruk
   * czyta wspólnik, bank i zarząd. Wolno go złożyć w każdym stanie sprawozdania.
   */
  generateStatementPdf: (companyId: string, reportId: string) =>
    request<AnnualReportFile>(`api/companies/${companyId}/annual-reports/${reportId}/statement-pdf`, {
      method: 'POST',
    }),
  statementPdfFile: (companyId: string, reportId: string) =>
    download(`api/companies/${companyId}/annual-reports/${reportId}/statement.pdf`),
  newReportVersion: (companyId: string, reportId: string, reason: string) =>
    request<AnnualReport>(`api/companies/${companyId}/annual-reports/${reportId}/versions`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  /** Kto ma podpisać, co już mamy i czego w podpisach nie sprawdzamy (docs/08, krok 8). */
  signaturePanel: (companyId: string, reportId: string) =>
    request<SignaturePanel>(`api/companies/${companyId}/annual-reports/${reportId}/signatures`),
  /** Plik do podpisania: z podpisami poprzedników, jeśli ktoś już podpisał. */
  fileToSign: (companyId: string, reportId: string) =>
    download(`api/companies/${companyId}/annual-reports/${reportId}/signatures/file`),
  addSignature: (
    companyId: string,
    reportId: string,
    file: File,
    params: {
      capacity: SignatureCapacity;
      method: SignatureMethod;
      officerId?: string;
      personName?: string;
      note?: string;
    },
  ) => {
    const query = new URLSearchParams({ capacity: params.capacity, method: params.method });
    if (params.officerId) query.set('officerId', params.officerId);
    if (params.personName) query.set('personName', params.personName);
    if (params.note) query.set('note', params.note);
    return upload<SignaturePanel>(
      `api/companies/${companyId}/annual-reports/${reportId}/signatures?${query.toString()}`,
      file,
    );
  },
  /** Treść oświadczenia albo odmowy z art. 52 ust. 2b — PDF do podpisania poza aplikacją. */
  declaration: (
    companyId: string,
    reportId: string,
    officerId: string,
    method: 'STATEMENT' | 'REFUSAL',
    note?: string,
  ) =>
    request<GeneratedDeclaration>(`api/companies/${companyId}/annual-reports/${reportId}/declarations`, {
      method: 'POST',
      body: JSON.stringify({ officerId, method, note: note ?? null }),
    }),

  /**
   * Sprawozdanie zarządu z działalności (docs/08, sekcja 7).
   *
   * Wraca z sekcjami, wskaźnikami liczonymi z tej samej projekcji co sprawozdanie finansowe
   * i z propozycjami akapitów z ksiąg. Propozycja **nie jest** treścią dokumentu, dopóki człowiek
   * jej nie przyjmie — dlatego wraca osobno od sekcji.
   */
  activityReport: (companyId: string, reportId: string) =>
    request<ActivityReportView>(`api/companies/${companyId}/annual-reports/${reportId}/activity-report`),
  saveActivityReport: (
    companyId: string,
    reportId: string,
    sections: { code: string; text: string }[],
    edited = true,
  ) =>
    request<ActivityReportView>(`api/companies/${companyId}/annual-reports/${reportId}/activity-report`, {
      method: 'PUT',
      body: JSON.stringify({ sections, edited }),
    }),
  /** Dokument do podpisu (PDF) albo do dopisania akapitów poza aplikacją (DOCX). */
  renderActivityReport: (companyId: string, reportId: string, format: 'pdf' | 'docx' = 'pdf') =>
    request<AnnualReportFile>(
      `api/companies/${companyId}/annual-reports/${reportId}/activity-report/render?format=${format}`,
      { method: 'POST' },
    ),
  activityReportFile: (companyId: string, reportId: string) =>
    download(`api/companies/${companyId}/annual-reports/${reportId}/activity-report/file`),

  /** Komplet uchwał do podjęcia, z kwotami z wyniku roku i z kapitałów. */
  resolutionPlan: (companyId: string, reportId: string) =>
    request<ResolutionPlan>(`api/companies/${companyId}/annual-reports/${reportId}/resolution-plan`),
  resolutions: (companyId: string, annualReportId?: string) =>
    request<Resolution[]>(
      `api/companies/${companyId}/resolutions${annualReportId ? `?annualReportId=${annualReportId}` : ''}`,
    ),
  createResolution: (
    companyId: string,
    body: {
      organ: ResolutionOrgan;
      kind: ResolutionKind;
      number?: string | null;
      adoptedOn?: string | null;
      fiscalYearId?: string | null;
      annualReportId?: string | null;
      params?: Record<string, string>;
    },
  ) => request<Resolution>(`api/companies/${companyId}/resolutions`, { method: 'POST', body: JSON.stringify(body) }),
  adoptResolution: (companyId: string, id: string, adoptedOn: string, number?: string | null) =>
    request<Resolution>(`api/companies/${companyId}/resolutions/${id}/adoption`, {
      method: 'POST',
      body: JSON.stringify({ adoptedOn, number: number ?? null }),
    }),
  resolutionPdf: (companyId: string, id: string) => download(`api/companies/${companyId}/resolutions/${id}/pdf`),
  /** Protokół zgromadzenia z listą obecności; powstaje z kartoteki wspólników i podjętych uchwał. */
  generateMinutes: (
    companyId: string,
    reportId: string,
    minutes: { meetingDate: string; place: string; chairman?: string; secretary?: string; unanimous: boolean },
  ) =>
    request<AnnualReportFile>(`api/companies/${companyId}/annual-reports/${reportId}/minutes`, {
      method: 'POST',
      body: JSON.stringify(minutes),
    }),
  minutesFile: (companyId: string, reportId: string) =>
    download(`api/companies/${companyId}/annual-reports/${reportId}/minutes`),

  /** Komplet do repozytorium: lista kontrolna, kto może złożyć, instrukcja i terminy. */
  rdfPackage: (companyId: string, reportId: string) =>
    request<RdfPackage>(`api/companies/${companyId}/annual-reports/${reportId}/package`),
  rdfPackageZip: (companyId: string, reportId: string) =>
    download(`api/companies/${companyId}/annual-reports/${reportId}/package.zip`),
  recordFiling: (
    companyId: string,
    reportId: string,
    filing: { submittedOn: string; submissionNumber?: string | null; kind?: Filing['kind'] },
  ) =>
    request<Filing>(`api/companies/${companyId}/annual-reports/${reportId}/filings`, {
      method: 'POST',
      body: JSON.stringify({ kind: 'RDF_FREE', ...filing }),
    }),
  uploadConfirmation: (companyId: string, reportId: string, file: File, confirmedOn: string) =>
    upload<Filing>(
      `api/companies/${companyId}/annual-reports/${reportId}/confirmation?confirmedOn=${confirmedOn}`,
      file,
    ),

  /** Terminarz roczny: przy każdym terminie data z ustawy i ostatni dzień roboczy przed nią. */
  annualCalendar: (companyId: string, fiscalYearId?: string) =>
    request<{ obligations: AnnualObligation[] }>(
      `api/companies/${companyId}/annual-calendar${fiscalYearId ? `?fiscalYearId=${fiscalYearId}` : ''}`,
    ),

  /** Kartoteka zarządu — okresami, nie stanem na dziś: uchwały za dany rok dotyczą tamtego zarządu. */
  officers: (companyId: string) => request<Officer[]>(`api/companies/${companyId}/officers`),
  addOfficer: (
    companyId: string,
    officer: {
      firstName: string;
      lastName: string;
      role: OfficerRole;
      fromDate: string;
      toDate?: string | null;
      peselInKrs: boolean;
    },
  ) => request<Officer>(`api/companies/${companyId}/officers`, { method: 'POST', body: JSON.stringify(officer) }),
  endOfficer: (companyId: string, id: string, toDate: string) =>
    request<void>(`api/companies/${companyId}/officers/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ toDate }),
    }),
  shareholders: (companyId: string) => request<Shareholder[]>(`api/companies/${companyId}/shareholders`),
  addShareholder: (
    companyId: string,
    shareholder: {
      name: string;
      shares: number;
      votes?: number | null;
      nominalValue: string;
      fromDate: string;
      toDate?: string | null;
    },
  ) =>
    request<Shareholder>(`api/companies/${companyId}/shareholders`, {
      method: 'POST',
      body: JSON.stringify(shareholder),
    }),
  endShareholder: (companyId: string, id: string, toDate: string) =>
    request<void>(`api/companies/${companyId}/shareholders/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ toDate }),
    }),

  /** Wzmianki w KRS: dowód złożenia niezależny od nas i od klienta (docs/08, sekcja 8). */
  krsRegister: (companyId: string) => request<RegisterStatus>(`api/companies/${companyId}/krs-register`),
  /** Pytanie do rejestru na żądanie; bez `force` serwer odpowiada tym, co wie z dzisiejszego pytania. */
  checkKrsRegister: (companyId: string, force = false) =>
    request<RegisterStatus>(`api/companies/${companyId}/krs-register?force=${force}`, { method: 'POST' }),
  /** Rok obrotowy klientów biura w jednej siatce — jedno zapytanie na cały panel. */
  officeAnnual: (officeId: string, year: number, onlyMine = false) =>
    request<OfficeAnnualPanel>(`api/offices/${officeId}/annual?year=${year}&onlyMine=${onlyMine}`),

  /** Archiwum oryginałów (E5-S2). */
  documents: (companyId: string, pending = false) =>
    request<ArchivedDocument[]>(`api/companies/${companyId}/documents${pending ? '?pending=true' : ''}`),
  uploadDocument: (companyId: string, file: File, kind: ArchivedDocument['kind'] = 'COST_SCAN') =>
    upload<ArchivedDocument>(`api/companies/${companyId}/documents?kind=${kind}`, file),
  /** Adres do otwarcia oryginału; `url` puste, gdy magazyn nie umie podpisanych odsyłaczy. */
  documentLink: (companyId: string, documentId: string) =>
    request<{ url: string | null; fileName: string }>(
      `api/companies/${companyId}/documents/${documentId}/link`,
    ),
  documentContent: (companyId: string, documentId: string) =>
    download(`api/companies/${companyId}/documents/${documentId}/content`),
  deleteDocument: (companyId: string, documentId: string) =>
    request<void>(`api/companies/${companyId}/documents/${documentId}`, { method: 'DELETE' }),
  documentWizard: (companyId: string, documentId: string) =>
    request<{ state: Record<string, unknown> | null }>(`api/companies/${companyId}/documents/${documentId}/wizard`),
  saveDocumentWizard: (companyId: string, documentId: string, state: Record<string, unknown>) =>
    request<{ state: Record<string, unknown> | null }>(`api/companies/${companyId}/documents/${documentId}/wizard`, {
      method: 'PUT',
      body: JSON.stringify(state),
    }),
  linkDocumentToCost: (companyId: string, documentId: string, costDocumentId: string) =>
    request<ArchivedDocument>(`api/companies/${companyId}/documents/${documentId}/link-cost`, {
      method: 'POST',
      body: JSON.stringify({ costDocumentId }),
    }),
  acceptDocument: (companyId: string, documentId: string) =>
    request<ArchivedDocument>(`api/companies/${companyId}/documents/${documentId}/accept`, { method: 'POST' }),
  rejectDocument: (companyId: string, documentId: string, reason: string) =>
    request<ArchivedDocument>(`api/companies/${companyId}/documents/${documentId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  intakeAddresses: (companyId: string) => request<IntakeAddress[]>(`api/companies/${companyId}/intake/addresses`),
  createIntakeAddress: (companyId: string, tag: string | null) =>
    request<IntakeAddress>(`api/companies/${companyId}/intake/addresses`, { method: 'POST', body: JSON.stringify({ tag }) }),
  revokeIntakeAddress: (companyId: string, id: string) =>
    request<void>(`api/companies/${companyId}/intake/addresses/${id}`, { method: 'DELETE' }),
  intakeSenders: (companyId: string) => request<IntakeSender[]>(`api/companies/${companyId}/intake/senders`),
  addIntakeSender: (companyId: string, email: string) =>
    request<IntakeSender>(`api/companies/${companyId}/intake/senders`, { method: 'POST', body: JSON.stringify({ email }) }),
  revokeIntakeSender: (companyId: string, id: string) =>
    request<void>(`api/companies/${companyId}/intake/senders/${id}`, { method: 'DELETE' }),

  invitationPreview: (token: string) => request<InvitationPreview>(`api/invitations/${token}`, {}, true),
  acceptInvitation: (token: string) =>
    request<Invitation>(`api/invitations/${token}/accept`, { method: 'POST' }),
  counterparties: (companyId: string, query: string, includeInactive: boolean) =>
    request<Counterparty[]>(
      `api/companies/${companyId}/counterparties?query=${encodeURIComponent(query)}&includeInactive=${includeInactive}`,
    ),
  addCounterparty: (companyId: string, counterparty: NewCounterparty) =>
    request<Counterparty>(`api/companies/${companyId}/counterparties`, {
      method: 'POST',
      body: JSON.stringify(counterparty),
    }),
  deactivateCounterparty: (companyId: string, id: string) =>
    request<void>(`api/companies/${companyId}/counterparties/${id}`, { method: 'DELETE' }),
  counterparty: (companyId: string, id: string) =>
    request<Counterparty>(`api/companies/${companyId}/counterparties/${id}`),
  products: (companyId: string, query: string, includeInactive: boolean) =>
    request<Product[]>(
      `api/companies/${companyId}/products?query=${encodeURIComponent(query)}&includeInactive=${includeInactive}`,
    ),
  addProduct: (companyId: string, product: NewProduct) =>
    request<Product>(`api/companies/${companyId}/products`, { method: 'POST', body: JSON.stringify(product) }),
  updateProduct: (companyId: string, id: string, product: NewProduct) =>
    request<Product>(`api/companies/${companyId}/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(product),
    }),
  deactivateProduct: (companyId: string, id: string) =>
    request<void>(`api/companies/${companyId}/products/${id}`, { method: 'DELETE' }),
  numbering: (companyId: string, year: number) =>
    request<NumberingSeries[]>(`api/companies/${companyId}/numbering?year=${year}`),
  setNumbering: (
    companyId: string,
    documentType: DocumentType,
    year: number,
    pattern: string,
    reset: NumberingReset,
  ) =>
    request<NumberingSeries>(`api/companies/${companyId}/numbering/${documentType}?year=${year}`, {
      method: 'PUT',
      body: JSON.stringify({ pattern, reset }),
    }),
  bankAccounts: (companyId: string) => request<BankAccount[]>(`api/companies/${companyId}/bank-accounts`),
  addBankAccount: (companyId: string, account: { iban: string; currency: string; purpose: AccountPurpose; bankName?: string }) =>
    request<BankAccount>(`api/companies/${companyId}/bank-accounts`, {
      method: 'POST',
      body: JSON.stringify(account),
    }),
  removeBankAccount: (companyId: string, id: string) =>
    request<void>(`api/companies/${companyId}/bank-accounts/${id}`, { method: 'DELETE' }),
  invoices: (companyId: string) => request<Invoice[]>(`api/companies/${companyId}/invoices`),
  invoice: (companyId: string, id: string) => request<Invoice>(`api/companies/${companyId}/invoices/${id}`),
  createInvoice: (companyId: string, draft: InvoiceDraft) =>
    request<Invoice>(`api/companies/${companyId}/invoices`, { method: 'POST', body: JSON.stringify(draft) }),
  updateInvoice: (companyId: string, id: string, draft: InvoiceDraft) =>
    request<Invoice>(`api/companies/${companyId}/invoices/${id}`, { method: 'PUT', body: JSON.stringify(draft) }),
  /** Faktura jako plik FA(3) do wgrania w KSeF (E4-S2). */
  invoiceKsefFile: (companyId: string, id: string) =>
    download(`api/companies/${companyId}/invoices/${id}/ksef`),
  invoiceCurrentState: (companyId: string, id: string) =>
    request<Invoice>(`api/companies/${companyId}/invoices/${id}/current-state`),
  correctInvoice: (companyId: string, id: string, correction: CorrectionRequest) =>
    request<Invoice>(`api/companies/${companyId}/invoices/${id}/corrections`, {
      method: 'POST',
      body: JSON.stringify(correction),
    }),
  /** Faktura jako PDF — ten sam dokument, który dostaje nabywca. */
  invoicePdf: (companyId: string, id: string) => download(`api/companies/${companyId}/invoices/${id}/pdf`),
  /** Zapłaty zapisane przy fakturze; jedna faktura może mieć ich kilka (wpłaty częściowe). */
  invoicePayments: (companyId: string, invoiceId: string) =>
    request<Payment[]>(`api/companies/${companyId}/invoices/${invoiceId}/payments`),
  deletePayment: (companyId: string, paymentId: string) =>
    request<void>(`api/companies/${companyId}/payments/${paymentId}`, { method: 'DELETE' }),
  /**
   * Notatka wewnętrzna przy fakturze.
   *
   * Jedyna zmiana dozwolona przy fakturze wystawionej: notatki nie ma ani na wydruku, ani w pliku
   * KSeF, więc nie zmienia dokumentu, który nabywca już dostał. Puste pole ją kasuje.
   */
  setInvoiceNote: (companyId: string, id: string, note: string | null) =>
    request<Invoice>(`api/companies/${companyId}/invoices/${id}/internal-note`, {
      method: 'PUT',
      body: JSON.stringify({ note }),
    }),
  issueInvoice: (companyId: string, id: string) =>
    request<Invoice>(`api/companies/${companyId}/invoices/${id}/issue`, { method: 'POST' }),
  deleteInvoice: (companyId: string, id: string) =>
    request<void>(`api/companies/${companyId}/invoices/${id}`, { method: 'DELETE' }),
  offices: () => request<Office[]>('api/offices'),
  createOffice: (nip: string, name: string) =>
    request<Office>('api/offices', { method: 'POST', body: JSON.stringify({ nip, name }) }),
  officeMembers: (officeId: string) => request<OfficeMember[]>(`api/offices/${officeId}/members`),
  officeClients: (officeId: string) => request<OfficeClient[]>(`api/offices/${officeId}/clients`),

  /**
   * Umowy tej firmy z biurami — propozycje i obowiązujące razem. Decyduje o nich właściciel
   * albo administrator firmy, także o zakończeniu: księgowego trzeba móc zwolnić.
   */
  officeAgreements: (companyId: string) =>
    request<OfficeClient[]>(`api/companies/${companyId}/office-agreements`),
  acceptOfficeProposal: (companyId: string, agreementId: string) =>
    request<OfficeClient>(`api/companies/${companyId}/office-agreements/${agreementId}/accept`, {
      method: 'POST',
    }),
  rejectOfficeProposal: (companyId: string, agreementId: string) =>
    request<void>(`api/companies/${companyId}/office-agreements/${agreementId}/reject`, { method: 'POST' }),
  endOfficeAgreement: (companyId: string, agreementId: string) =>
    request<void>(`api/companies/${companyId}/office-agreements/${agreementId}/end`, { method: 'POST' }),
  officeDashboard: (officeId: string, month: string, onlyMine: boolean) =>
    request<OfficeDashboard>(`api/offices/${officeId}/dashboard?month=${month}&onlyMine=${onlyMine}`),
  officeAssignments: (officeId: string) =>
    request<ClientAssignment[]>(`api/offices/${officeId}/assignments`),
  assignAccountant: (officeId: string, agreementId: string, userId: string, role: AssignmentRole) =>
    request<ClientAssignment>(`api/offices/${officeId}/clients/${agreementId}/assignments`, {
      method: 'POST',
      body: JSON.stringify({ userId, role }),
    }),
  endAssignment: (officeId: string, assignmentId: string) =>
    request<void>(`api/offices/${officeId}/assignments/${assignmentId}`, { method: 'DELETE' }),
  costs: (companyId: string) => request<CostDocument[]>(`api/companies/${companyId}/costs`),
  addCost: (companyId: string, cost: NewCost) =>
    request<CostDocument>(`api/companies/${companyId}/costs`, { method: 'POST', body: JSON.stringify(cost) }),
  correctCost: (companyId: string, id: string, correction: NewCostCorrection) =>
    request<CostDocument>(`api/companies/${companyId}/costs/${id}/corrections`, {
      method: 'POST',
      body: JSON.stringify(correction),
    }),
  bookCost: (companyId: string, id: string) =>
    request<CostDocument>(`api/companies/${companyId}/costs/${id}/book`, { method: 'POST' }),
  deleteCost: (companyId: string, id: string) =>
    request<void>(`api/companies/${companyId}/costs/${id}`, { method: 'DELETE' }),
  importBankStatement: (companyId: string, content: string) =>
    request<ImportResult>(`api/companies/${companyId}/bank/statements`, {
      method: 'POST',
      body: content,
      headers: { 'Content-Type': 'text/plain' },
    }),
  bankTransactions: (companyId: string) =>
    request<TransactionWithProposals[]>(`api/companies/${companyId}/bank/transactions`),
  matchBankTransaction: (companyId: string, transactionId: string, documentId: string, amount?: string) =>
    request<BankTransaction>(`api/companies/${companyId}/bank/transactions/${transactionId}/match`, {
      method: 'POST',
      body: JSON.stringify({ documentId, amount }),
    }),
  inventories: (companyId: string, year: number) =>
    request<Inventory[]>(`api/companies/${companyId}/inventories?year=${year}`),
  inventory: (companyId: string, id: string) =>
    request<Inventory>(`api/companies/${companyId}/inventories/${id}`),
  addInventory: (companyId: string, kind: InventoryKind, takenOn: string, items: NewInventoryItem[]) =>
    request<Inventory>(`api/companies/${companyId}/inventories`, {
      method: 'POST',
      body: JSON.stringify({ kind, takenOn, items }),
    }),
  replaceInventoryItems: (companyId: string, id: string, items: NewInventoryItem[]) =>
    request<Inventory>(`api/companies/${companyId}/inventories/${id}/items`, {
      method: 'PUT',
      body: JSON.stringify(items),
    }),
  approveInventory: (companyId: string, id: string) =>
    request<Inventory>(`api/companies/${companyId}/inventories/${id}/approve`, { method: 'POST' }),
  deleteInventory: (companyId: string, id: string) =>
    request<void>(`api/companies/${companyId}/inventories/${id}`, { method: 'DELETE' }),
  /** Rozliczenie roczne: jedno przy działalności jednoosobowej, po jednym na wspólnika w spółce. */
  pitAnnual: (companyId: string, year: number, priorYearLoss?: string) =>
    request<PitAnnual[]>(
      `api/companies/${companyId}/pit-annual?year=${year}` +
        (priorYearLoss ? `&priorYearLoss=${encodeURIComponent(priorYearLoss)}` : ''),
    ),
  /** Roczne rozliczenie składki zdrowotnej: lista, bo w spółce osobowej rozlicza się każdy wspólnik. */
  healthAnnual: (companyId: string, year: number) =>
    request<HealthAnnual[]>(`api/companies/${companyId}/health-annual?year=${year}`),
  suspensions: (companyId: string) => request<SuspensionView[]>(`api/companies/${companyId}/suspensions`),
  addSuspension: (companyId: string, suspension: NewSuspension) =>
    request<SuspensionView>(`api/companies/${companyId}/suspensions`, {
      method: 'POST',
      body: JSON.stringify(suspension),
    }),
  resumeBusiness: (companyId: string, id: string, lastDay: string) =>
    request<SuspensionView>(`api/companies/${companyId}/suspensions/${id}/resume`, {
      method: 'POST',
      body: JSON.stringify({ lastDay }),
    }),
  removeSuspension: (companyId: string, id: string, reason: string) =>
    request<void>(`api/companies/${companyId}/suspensions/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),
  annualIncome: (companyId: string, year: number) =>
    request<AnnualIncome>(`api/companies/${companyId}/annual-income?year=${year}`),
  /** Pełny eksport danych firmy; zwracamy tekst, bo plik zapisujemy od razu na dysk */
  exportCompany: async (companyId: string): Promise<Blob> => {
    const data = await request<unknown>(`api/companies/${companyId}/export`);
    return new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  },
  assets: (companyId: string, includeDisposed = false) =>
    request<FixedAsset[]>(`api/companies/${companyId}/assets?includeDisposed=${includeDisposed}`),
  asset: (companyId: string, id: string) =>
    request<AssetWithPlan>(`api/companies/${companyId}/assets/${id}`),
  addAsset: (companyId: string, asset: NewAsset) =>
    request<FixedAsset>(`api/companies/${companyId}/assets`, { method: 'POST', body: JSON.stringify(asset) }),
  disposeAsset: (companyId: string, id: string, disposedOn: string, reason: string) =>
    request<AssetDisposal>(`api/companies/${companyId}/assets/${id}/disposal`, {
      method: 'POST',
      body: JSON.stringify({ disposedOn, reason }),
    }),
  bookDepreciation: (companyId: string, period: string) =>
    request<DepreciationRun>(`api/companies/${companyId}/assets/depreciation/run?period=${period}`, {
      method: 'POST',
    }),
  /** Co jest do wyceny na dany dzień i jakich kursów brakuje (E7-S5). */
  fxRevaluation: (companyId: string, asOf: string) =>
    request<FxRevaluation>(`api/companies/${companyId}/fx-revaluation?asOf=${asOf}`),
  /** `dryRun` liczy różnice i niczego nie księguje — podgląd przed zapisem. */
  revalueCurrencies: (companyId: string, asOf: string, rates: Record<string, string>, dryRun: boolean) =>
    request<FxRevaluation>(`api/companies/${companyId}/fx-revaluation`, {
      method: 'POST',
      body: JSON.stringify({ asOf, rates, dryRun }),
    }),
  accruals: (companyId: string) => request<AccrualView[]>(`api/companies/${companyId}/accruals`),
  addAccrual: (companyId: string, accrual: NewAccrual) =>
    request<AccrualView>(`api/companies/${companyId}/accruals`, {
      method: 'POST',
      body: JSON.stringify(accrual),
    }),
  releaseAccruals: (companyId: string, period: string) =>
    request<AccrualRun>(`api/companies/${companyId}/accruals/run?period=${period}`, { method: 'POST' }),
  removeAccrual: (companyId: string, id: string, reason: string) =>
    request<void>(`api/companies/${companyId}/accruals/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),
  provisions: (companyId: string) => request<ProvisionView[]>(`api/companies/${companyId}/provisions`),
  addProvision: (companyId: string, provision: NewProvision) =>
    request<ProvisionView>(`api/companies/${companyId}/provisions`, {
      method: 'POST',
      body: JSON.stringify(provision),
    }),
  accrueProvisions: (companyId: string, period: string) =>
    request<ProvisionRun>(`api/companies/${companyId}/provisions/run?period=${period}`, { method: 'POST' }),
  settleProvision: (companyId: string, id: string, settlement: SettleProvision) =>
    request<ProvisionView>(`api/companies/${companyId}/provisions/${id}/settle`, {
      method: 'POST',
      body: JSON.stringify(settlement),
    }),
  removeProvision: (companyId: string, id: string, reason: string) =>
    request<void>(`api/companies/${companyId}/provisions/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),
  receivables: (companyId: string, onlyOpen = true) =>
    request<Settlement[]>(`api/companies/${companyId}/receivables?onlyOpen=${onlyOpen}`),
  payables: (companyId: string, onlyOpen = true) =>
    request<Settlement[]>(`api/companies/${companyId}/payables?onlyOpen=${onlyOpen}`),
  payInvoice: (companyId: string, invoiceId: string, payment: NewPayment) =>
    request<unknown>(`api/companies/${companyId}/invoices/${invoiceId}/payments`, {
      method: 'POST',
      body: JSON.stringify(payment),
    }),
  payCost: (companyId: string, costId: string, payment: NewPayment) =>
    request<unknown>(`api/companies/${companyId}/costs/${costId}/payments`, {
      method: 'POST',
      body: JSON.stringify(payment),
    }),
  billing: (companyId: string) => request<BillingMonth[]>(`api/companies/${companyId}/billing`),
  paymentProviders: (scope: PayerScope) =>
    request<PaymentProvider[]>(`api/${scope.kind}/${scope.id}/payment-methods/providers`),
  paymentMandates: (scope: PayerScope) => request<PaymentMandate[]>(`api/${scope.kind}/${scope.id}/payment-methods`),
  paymentCharges: (scope: PayerScope) => request<PaymentCharge[]>(`api/${scope.kind}/${scope.id}/payment-methods/charges`),
  paymentArrears: (scope: PayerScope) => request<PaymentArrear[]>(`api/${scope.kind}/${scope.id}/payment-methods/arrears`),
  startMandate: (scope: PayerScope, provider: string, method: PaymentMethodKind, returnUrl: string) =>
    request<MandateStart>(`api/${scope.kind}/${scope.id}/payment-methods`, {
      method: 'POST',
      body: JSON.stringify({ provider, method: method.toUpperCase(), returnUrl }),
    }),
  confirmMandate: (scope: PayerScope, mandateId: string, token: string, label: string | null) =>
    request<void>(`api/${scope.kind}/${scope.id}/payment-methods/${mandateId}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ token, label }),
    }),
  revokeMandate: (scope: PayerScope, mandateId: string) =>
    request<void>(`api/${scope.kind}/${scope.id}/payment-methods/${mandateId}`, { method: 'DELETE' }),
  officeBilling: (officeId: string, period: string) =>
    request<OfficeBillingStatement>(`api/offices/${officeId}/billing?period=${period}`),
  billingPreview: (companyId: string, period: string) =>
    request<Fee>(`api/companies/${companyId}/billing/preview?period=${period}`),
  companyMonth: (companyId: string, period: string) =>
    request<CompanyMonth>(`api/companies/${companyId}/month?period=${period}`),
  ryczaltEvidence: (companyId: string, month: string) =>
    request<RyczaltEvidence>(`api/companies/${companyId}/ryczalt-evidence?month=${month}`),
  ledger: (companyId: string, from: string, to: string) =>
    request<LedgerReport>(`api/companies/${companyId}/ledger?from=${from}&to=${to}`),
  vatRegisters: (companyId: string, month: string) =>
    request<VatRegisters>(`api/companies/${companyId}/vat-registers?month=${month}`),
  badDebts: (companyId: string, period: string, tax: BadDebtTax) =>
    request<BadDebtReview>(`api/companies/${companyId}/bad-debts?period=${period}&tax=${tax}`),
  recordBadDebts: (companyId: string, period: string, tax: BadDebtTax, decisions: BadDebtDecision[]) =>
    request<BadDebtReview>(`api/companies/${companyId}/bad-debts`, {
      method: 'POST',
      body: JSON.stringify({ period, tax, decisions }),
    }),
  undoBadDebt: (companyId: string, id: string) =>
    request<void>(`api/companies/${companyId}/bad-debts/${id}`, { method: 'DELETE' }),
  vatUe: (companyId: string, period: string) =>
    request<VatUeSummary>(`api/companies/${companyId}/vat-ue?period=${period}`),
  /** Plik VAT-UE pobieramy żądaniem z tokenem, tak samo jak JPK. */
  vatUeXml: (companyId: string, period: string) =>
    download(`api/companies/${companyId}/vat-ue/xml?period=${period}`),
  viesChecks: (companyId: string) => request<ViesCheck[]>(`api/companies/${companyId}/vies`),
  /** Historia sprawdzeń jednego kontrahenta, od najnowszego — pierwszy wiersz to stan na dziś. */
  viesHistory: (companyId: string, counterpartyId: string) =>
    request<ViesCheck[]>(`api/companies/${companyId}/vies/${counterpartyId}`),
  checkVies: (companyId: string, counterpartyId: string) =>
    request<ViesCheck>(`api/companies/${companyId}/vies/${counterpartyId}`, { method: 'POST' }),
  /** Sprawdza wszystkich kontrahentów z informacji VAT-UE za miesiąc — jedno kliknięcie przed wysyłką. */
  checkViesForPeriod: (companyId: string, period: string) =>
    request<ViesBatch>(`api/companies/${companyId}/vies?period=${period}`, { method: 'POST' }),
  taxOffices: () => request<TaxOffice[]>('api/tax-offices'),
  filingDetails: (companyId: string) =>
    request<FilingDetails>(`api/companies/${companyId}/filing-details`),
  saveFilingDetails: (companyId: string, details: FilingDetails) =>
    request<FilingDetails>(`api/companies/${companyId}/filing-details`, {
      method: 'PUT',
      body: JSON.stringify(details),
    }),
  /**
   * Plik JPK_V7M. Pobieramy go żądaniem z tokenem, a nie zwykłym odsyłaczem — przeglądarka
   * nie dołożyłaby do odsyłacza nagłówka uwierzytelniającego.
   */
  jpkV7m: (companyId: string, period: string) =>
    download(`api/companies/${companyId}/jpk/v7m?period=${period}`),
  jpkSubmissions: (companyId: string) => request<JpkSubmission[]>(`api/companies/${companyId}/jpk/submissions`),
  /** Bez `income` wysyłka czeka na podpisany plik metadanych; z nim — idzie od razu (osoba fizyczna). */
  submitJpk: (companyId: string, period: string, income: string | null) =>
    request<JpkSubmission>(`api/companies/${companyId}/jpk/submissions`, {
      method: 'POST',
      body: JSON.stringify({ period, authorization: income ? { income } : null }),
    }),
  jpkInitUpload: (companyId: string, id: string) => download(`api/companies/${companyId}/jpk/submissions/${id}/init-upload`),
  jpkSigned: (companyId: string, id: string, signedXml: string) =>
    request<JpkSubmission>(`api/companies/${companyId}/jpk/submissions/${id}/signed`, {
      method: 'POST',
      body: signedXml,
      headers: { 'Content-Type': 'application/xml' },
    }),
  officeJpkPrepare: (officeId: string, period: string, companyIds: string[]) =>
    request<{ prepared: (JpkSubmission & { companyId: string })[]; problems: Record<string, string> }>(`api/offices/${officeId}/jpk/submissions`, {
      method: 'POST',
      body: JSON.stringify({ period, companyIds }),
    }),
  officeJpkSubmissions: (officeId: string, period: string) =>
    request<(JpkSubmission & { companyId: string })[]>(`api/offices/${officeId}/jpk/submissions?period=${period}`),
  officeJpkToSign: (officeId: string, period: string) => download(`api/offices/${officeId}/jpk/submissions/to-sign.zip?period=${period}`),
  jpkUpo: (companyId: string, id: string) => download(`api/companies/${companyId}/jpk/submissions/${id}/upo`),
  /** Deklaracja ZUS DRA do wczytania w eZUS albo w Płatniku — plik, nie wysyłka. */
  zusDra: (companyId: string, period: string) =>
    download(`api/companies/${companyId}/zus/dra?period=${period}`),
  settings: (companyId: string) => request<CompanySettings[]>(`api/companies/${companyId}/settings`),
  addSettings: (companyId: string, settings: NewSettings) =>
    request<CompanySettings>(`api/companies/${companyId}/settings`, {
      method: 'POST',
      body: JSON.stringify(settings),
    }),
  /**
   * Poprawienie istniejącego wpisu, razem z dniem obowiązywania. Zmiana decyzji to nadal nowy
   * wpis — ten adres jest dla pomyłki przy wpisywaniu.
   */
  changeSettings: (companyId: string, settingsId: string, settings: NewSettings) =>
    request<CompanySettings>(`api/companies/${companyId}/settings/${settingsId}`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
};
